import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Plus, X, AlertTriangle, Wrench, ClipboardCheck, DollarSign, CreditCard, Camera, ImagePlus, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { ReturnVisitFields, emptyReturnVisit, returnVisitPayload, returnVisitError } from './completion/ReturnVisitFields';
import AssetSystemsStep from './completion/AssetSystemsStep';
import MembershipPlanPicker from './MembershipPlanPicker';
import { useDurableDraft } from '../hooks/useDurableDraft';
import DraftStatus from './DraftStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iter113 — Simplified Invoice Decision list (was 7 choices, now 6 with a clearer hierarchy).
// "Customer Not Available" + "Deferred Payment" merged into one "Deferred / Unable to Collect"
// decision; the reason is captured on the payment step. "Warranty Claim" renamed to the
// product-correct "Warranty Invoice" since we're billing the warranty company, not just filing.
const INVOICE_DECISIONS = [
  { key: 'create_invoice',           label: 'Create Invoice Now',           hint: 'Bill and collect from the customer right now',          needsInvoice: true,  needsPayment: true  },
  { key: 'save_draft',               label: 'Save Draft Invoice',           hint: 'Office will finalize later',                           needsInvoice: true,  needsPayment: false },
  // iter113 — "Warranty Invoice" is more accurate than "Warranty Claim": NexFlow is
  // creating + billing an invoice to the warranty/HSP company, not just filing a claim.
  { key: 'hsp_claim',                label: 'Warranty Invoice',             hint: 'HSP / warranty-company billing. Office will generate the warranty invoice separately.', needsInvoice: true,  needsPayment: true, needsHspDetails: true },
  // iter156 — Membership Included / $0 Invoice. Customer-linked service record, no charge.
  { key: 'membership_included',      label: 'Membership Included / $0 Invoice', hint: 'Covered under active NexFlow membership. Creates a customer service record with $0 due.', needsInvoice: true, needsPayment: false },
  { key: 'nexflow_warranty',         label: 'NexFlow Warranty / Callback',  hint: 'Covered under NexFlow warranty. No customer charge, no warranty-company invoice.', needsInvoice: false, needsPayment: false },
  { key: 'return_visit_needed',      label: 'Return Visit Needed',          hint: 'Work not complete — bill on the next visit',           needsInvoice: false, needsPayment: false },
  // iter113 — Merged "Customer Not Available" + "Deferred Payment (mgr-approved)".
  // Reason is collected on the payment-disposition step (or here if no invoice needs to be created).
  { key: 'deferred_unable_collect',  label: 'No Payment Collected — Office to Bill', hint: 'Combine — customer not available, manager-approved deferral, payment link to be sent, waiting on approval, etc.', needsInvoice: true, needsPayment: true },
  // iter184 — New tech-facing outcomes that close the "blank billing" loophole.
  { key: 'estimate_only',            label: 'Estimate Only — No Invoice Today', hint: 'Quote given, no work performed. No invoice, no payment expected today.', needsInvoice: false, needsPayment: false },
  { key: 'office_review_required',   label: 'Office to Finalize & Submit Invoice',   hint: 'Work is complete, but the office must review, finalize, or send the invoice. Enter the billing details next.', needsInvoice: false, needsPayment: false },
];

// iter184 — Simplified 6-option Billing Outcome picker shown on Page 1 of the
// completion wizard. Every completion MUST pick one before submit. The chosen
// key maps 1:1 to a COMPLETION_INVOICE_STATUSES value and is auto-committed
// through the existing decision-step handler after Page 1 submit succeeds.
const BILLING_OUTCOMES = [
  { key: 'create_invoice',           label: 'Invoice Submitted / Payment Collected On Site', hint: 'Submit the customer invoice and record any payment collected during the visit.' },
  { key: 'hsp_claim',                label: 'Warranty / HSP Draft Created',     hint: 'Warranty or HSP claim — office finalizes the warranty invoice.' },
  { key: 'estimate_only',            label: 'Estimate Only',                    hint: 'Quote given, no work today.' },
  // iter206 — 'Office to Finalize & Submit Invoice' (the iter205 office-review workflow)
  // is hidden pending the user's production validation; the internal
  // `office_review_required` status + all backend/queue code stay intact.
  // iter304 — NexFlow workmanship-warranty / callback No-Charge outcome is now LIVE.
  // Reuses the canonical `nexflow_warranty` status: no invoice, no payment, counts as
  // billing-resolved (never enters Billing Missing). See NEXFLOW_WARRANTY_REASONS.
  { key: 'nexflow_warranty',         label: 'NexFlow Warranty / Callback — No Charge', hint: 'Corrective work covered by NexFlow workmanship warranty. No customer or third-party charge.' },
  { key: 'office_review_required',   label: 'Office to Finalize & Submit Invoice',   hint: 'Work is complete — the office reviews, finalizes & sends the invoice. Enter billing details next.', hidden: true },
];
const VISIBLE_BILLING_OUTCOMES = BILLING_OUTCOMES.filter((o) => !o.hidden);
// iter304 — Optional reason category for the NexFlow Warranty / Callback no-charge outcome.
const NEXFLOW_WARRANTY_REASONS = [
  { value: 'workmanship_callback', label: 'Workmanship callback' },
  { value: 'warranty_repair',      label: 'Warranty repair' },
  { value: 'return_visit',         label: 'Return visit / corrective work' },
];

// iter156 — Default editable line items prefilled when the tech picks
// "Membership Included / $0 Invoice". These are common membership perks; the tech
// can edit/add/remove any line before saving. Unit prices are zero — the total is
// always forced to $0.00 server-side regardless of what's typed here.
const MEMBERSHIP_INCLUDED_DEFAULT_LINES = [
  'Membership inspection',
  'Water heater flush',
  'Sump pump check',
  'Water pressure check',
  'Hose bib review',
  'Basic leak inspection',
  'Equipment notes / recommendations',
];

// iter114 — Payment workflow reset per user feedback. Stripe "Customer Paying Card
// Now" is HIDDEN from the technician picker (backend endpoints kept intact for a
// future tap-to-pay revival). The team uses QuickBooks Pay / GoPayment for field
// card collection — the tech records it as "Customer Paid — Other" with the right
// method_detail.
// iter115 — Also hide "Payment Link Sent" from the tech-facing picker until the
// Stripe/SMS payment-link path is fully debugged. The admin /admin/invoices Send
// Payment Link button (BillingDetailSheet) stays — that's the office-side workflow,
// not the technician closeout flow.
const PAYMENT_DISPOSITIONS = [
  { key: 'paid_cash',                label: 'Paid — Cash',        icon: DollarSign,    group: 'paid' },
  { key: 'paid_check',               label: 'Paid — Check',       icon: DollarSign,    group: 'paid' },
  { key: 'paid_card',                label: 'Paid — Card',        icon: CreditCard,    group: 'paid' },
  { key: 'paid_quickbooks',          label: 'Paid — QuickBooks',  icon: DollarSign,    group: 'paid' },
  { key: 'deferred_unable_collect',  label: 'Not Paid — Office to Bill', icon: ClipboardCheck, hint: 'Pick a reason — customer unavailable / manager approved / waiting on approval / etc.' },
  { key: 'no_balance_due',           label: 'No Customer Balance Due',  icon: ClipboardCheck, hint: 'NexFlow warranty, fully covered claim, or $0 invoice' },
];
// iter240 — dispositions where the tech collected/confirmed money on site (amount required).
const PAID_ONSITE_KEYS = ['paid_cash', 'paid_check', 'paid_card', 'paid_quickbooks', 'paid_other'];

// iter70/iter73 — Warranty companies are now pulled live from customer records where
// customer_type='warranty_company'. The completion wizard fetches them when the tech reaches the
// HSP details step. Free-text "Other (manual entry)" is still allowed and flagged for office review.

const ASSET_TYPES = [
  { key: 'water_heater',       label: 'Water Heater' },
  { key: 'tankless_heater',    label: 'Tankless'     },
  { key: 'water_softener',     label: 'Softener'     },
  { key: 'sump_pump',          label: 'Sump Pump'    },
  { key: 'battery_backup',     label: 'Battery Backup' },
  { key: 'ejector_pump',       label: 'Ejector Pump' },
  { key: 'garbage_disposal',   label: 'Disposal'     },
  { key: 'backflow_preventer', label: 'Backflow'     },
  { key: 'well_pump',          label: 'Well Pump'    },
  { key: 'well_tank',          label: 'Well Tank'    },
  { key: 'pressure_tank',      label: 'Pressure Tank' },
  { key: 'other',              label: 'Other'        },
];

const newAsset = () => ({
  type: 'water_heater', brand: '', model: '', serial: '', location: '',
  install_date: '', warranty_until: '', condition: 'good', notes: '',
  recommended_replacement_date: '',
  photo_urls: [],          // [{kind, data_url}]  kind=equipment|data_plate|installed
});

/**
 * CompletionWizard — props:
 *  - appointment: { id, customer_id, service_type, employee_department }
 *  - onClose(submitted: bool)
 */
const CompletionWizard = ({ appointment, onClose, initialOutcome = '' }) => {
  // step: 'form' | 'decision' | 'hsp_details' | 'invoice' | 'payment' | 'done'
  const [step, setStep] = useState('form');
  // iter69 — track navigation history so Back buttons can pop precisely without losing entered data.
  const [stepHistory, setStepHistory] = useState([]);
  const goToStep = (next) => { setStepHistory((h) => [...h, step]); setStep(next); };
  const goBack = () => {
    setStepHistory((h) => {
      if (h.length === 0) return h;
      const last = h[h.length - 1];
      setStep(last);
      return h.slice(0, -1);
    });
  };
  const [completionId, setCompletionId] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [pickedDecision, setPickedDecision] = useState(null);
  // iter112 — Extended invoice form: per-line `kind` (service|membership), an explicit
  // `membership_discount_percent` (defaults to 0 unless the customer is already a
  // member or the tech adds a membership line — auto-prefilled to 10% in those cases).
  const [invoiceForm, setInvoiceForm] = useState({
    amount: '',
    line_items: [],
    discount: 0,
    membership_savings: 0,
    membership_discount_percent: 0,
    notes: '',
    save_as_draft: false,
  });
  // iter114 — Default flipped from 'paying_card_now' → 'paid_cash' since the Stripe
  // card-now option is no longer in the technician picker.
  const [paymentForm, setPaymentForm] = useState({
    disposition: 'paid_cash', amount: '', reference: '', notes: '', payment_method_detail: '',
  });
  // iter113 — Stripe "Customer Paying Card Now" session state. The wizard creates
  // a Stripe Checkout session on demand and polls the invoice paid state until
  // the webhook flips it. Once paid=true, the close button activates and the
  // disposition is auto-submitted as 'paid_card' (Stripe-confirmed).
  // iter114 — Picker hides this disposition; state kept for a future revival.
  const [cardNow, setCardNow] = useState({ url: null, sessionId: null, paid: false, polling: false, opening: false });
  // iter114 — Post-close membership asset-log flow. If a membership line was on the
  // invoice, after the tech closes the job we prompt: "Did the customer buy a
  // membership today?" → if yes, Complete Asset Log Now OR Schedule Follow-Up.
  // iter119 — Renamed to Membership Onboarding. Three options now (Schedule
  // Onboarding Inspection / Quick Setup Now / Office Follow-Up Needed) backed
  // by /api/jobs/completion/{id}/membership-onboarding so status is tracked on
  // the customer record.
  const [invoiceHadMembership, setInvoiceHadMembership] = useState(false);
  const [showMembershipPrompt, setShowMembershipPrompt] = useState(false);
  const [membershipAssetMode, setMembershipAssetMode] = useState(null);  // null | 'schedule' | 'quick' | 'office_followup'
  const [includeWHFlush, setIncludeWHFlush] = useState(true);            // iter119 default ON
  const [onboardingPreferredDate, setOnboardingPreferredDate] = useState('');
  const [onboardingNotes, setOnboardingNotes] = useState('');
  const [assetSubmitting, setAssetSubmitting] = useState(false);
  // iter112 — Customer-member context loaded once we know who the customer is.
  // Drives auto-prefill of the membership discount % on the invoice step.
  const [customerHasActiveMembership, setCustomerHasActiveMembership] = useState(false);
  const [customerMembership, setCustomerMembership] = useState(null);  // iter298 — full status for picker guard
  const [showMembershipPicker, setShowMembershipPicker] = useState(false);  // iter298
  // iter69 — HSP claim fields captured BEFORE the customer invoice step.
  // iter73 — warranty_customer_id points to a Warranty Company customer record (preferred path).
  //          When tech picks "Other (manual entry)", warranty_customer_id stays null and the office
  //          gets a "NEW WARRANTY COMPANY" review badge on the draft invoice.
  const [hspForm, setHspForm] = useState({
    provider: '', work_order_number: appointment?.work_order_number || '',
    approved_amount: '', deductible: '', uncovered_balance: '', notes: '',
    warranty_customer_id: null,
  });
  // iter302 — Server-computed payer split (approved/deductible/uncovered/net_warranty/
  // customer_core) returned by the invoice-decision call. Drives the invoice-step
  // reconciliation panel and the warranty-only ($0 customer) path.
  const [hspAllocation, setHspAllocation] = useState(null);
  // iter122 — Dispatcher's master-schedule Work Order # is the source of truth.
  // We seed the warranty form with it AND auto-pick the Warranty Invoice decision
  // when present. If the tech tries to change the WO# in the field, we pop a red
  // warning modal once per wizard session. "No" reverts to the dispatcher value.
  const dispatcherWO = appointment?.work_order_number || '';
  const [woEditAck, setWoEditAck] = useState(false);
  const [woConfirmOpen, setWoConfirmOpen] = useState(false);
  const autoPickedWarrantyRef = useRef(false);
  // iter122 — Forward-reference for pickDecision so the auto-pick effect (which
  // must live before the `if (!appointment) return null` early-return) can call
  // into the handler that's declared later in this component.
  const pickDecisionRef = useRef(null);
  const [warrantyCompanies, setWarrantyCompanies] = useState([]);
  const [loadingWarrantyCos, setLoadingWarrantyCos] = useState(false);

  const [template, setTemplate] = useState(null);
  const [base, setBase] = useState({
    work_performed: '', materials_used: '', hours_worked: '', customer_po_number: '',
    recommended_followups: '', customer_signature_name: appointment?.customer_name || '',
  });
  const [answers, setAnswers] = useState({});
  const [opportunityFlags, setOpportunityFlags] = useState([]);
  const [equipment, setEquipment] = useState([]);
  // iter74 — slim Page 1: basic checklist + quick equipment notes. Replaces the full template-driven
  // category list (membership inspection / full equipment survey / sales-opportunity checklist /
  // aging-equipment flags) on the standard completion. The richer flow will live in a dedicated
  // Customer / Membership Inspection workflow later.
  const SHOW_FULL_INSPECTION = false;
  const [basicChecklist, setBasicChecklist] = useState({
    cleaned_work_area: false,
    tested_repair: false,
    explained_work: false,
    // iter112 — Customer satisfied is now a tri-state STRING ('yes' | 'no' | 'na' | '')
    // not a checkbox. Review request can only fire when value === 'yes'.
    customer_satisfied: '',
  });
  const [quickEquipment, setQuickEquipment] = useState({
    water_heater_age: '',
    water_heater_size: '',
    major_concern: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [assetError, setAssetError] = useState(null);   // set by AssetSystemsStep; blocks submit
  // iter184 — Mandatory Billing Outcome picker on Page 1. Blocks completion
  // submission if blank. Tech unsure of outcome picks "office_review_required"
  // → job stays visible on Billing Missing until office resolves.
  const [billingOutcome, setBillingOutcome] = useState(initialOutcome || '');
  // iter304 — reason category captured when the NexFlow Warranty / Callback no-charge
  // outcome is selected (optional).
  const [nexflowWarrantyReason, setNexflowWarrantyReason] = useState('');
  // iter205 — Office Billing Submission (tech hands completed job to office to finalize/send).
  const [officeBilling, setOfficeBilling] = useState({
    amount_basis: '', proposed_amount: '', amount_already_approved: '', estimate_id: '',
    invoice_description: '', labor_notes: '', labor_hours: '', materials: '', equipment_notes: '',
    payment_terms: '', reason_not_collected: '', po_number: '', billing_notes: '',
    additional_work: false, additional_work_notes: '', customer_approval_name: '',
  });
  const [officeSubmitResult, setOfficeSubmitResult] = useState(null);
  // Estimate-Only workflow — builder + confirmation.
  const [estimateForm, setEstimateForm] = useState({
    title: '', scope: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0, kind: 'service' }],
    customer_notes: '', internal_notes: '', expiration_date: '', follow_up_date: '', discount: 0,
  });
  const [estimateResult, setEstimateResult] = useState(null);
  // iter62 — tech-controlled review-request toggle.
  // iter64 (pre-launch tightening): default flipped to Skip — tech must intentionally opt in.
  const [sendReviewRequest, setSendReviewRequest] = useState(false);
  // iter316 — Visit outcome: 'job_complete' (normal billing closeout) or 'return_visit'
  // (non-final: Visit done, Job stays open, office schedules the return).
  const [visitOutcome, setVisitOutcome] = useState('job_complete');
  const [returnVisit, setReturnVisit] = useState(emptyReturnVisit());

  // Durable closeout draft (device + server), scoped to employee + Job + Visit + workflow so one
  // Job can never fill another. Survives backgrounding, reloads, auth/clock refresh, lost signal
  // and remounts; cleared ONLY on successful completion/submission or explicit Discard Draft.
  const DRAFT_SCOPE = appointment?.id
    ? `closeout:${appointment.job_id || 'nojob'}:${appointment.id}:${initialOutcome === 'estimate_only' ? 'estimate' : 'closeout'}`
    : null;
  const restoredDraftRef = useRef(null);
  const draftSnap = { base, basicChecklist, quickEquipment, invoiceForm, paymentForm, hspForm, officeBilling, estimateForm,
    billingOutcome, nexflowWarrantyReason, sendReviewRequest, visitOutcome, returnVisit,
    equipment: equipment.map((e) => ({ ...e, photo_urls: (e.photo_urls || []).filter((u) => !String(u).startsWith('data:')) })) };
  const draft = useDurableDraft({
    scopeKey: DRAFT_SCOPE,
    value: draftSnap,
    legacyKeys: appointment?.id ? [`nexflow_closeout_draft_v2__${appointment.job_id || 'nojob'}__${appointment.id}__closeout`,
      `nexflow_completion_draft_v1__${appointment.id}`] : [],
    onRestore: (d) => {
      restoredDraftRef.current = d;
      if (d.base)            setBase((b) => ({ ...b, ...d.base }));
      if (d.basicChecklist)  setBasicChecklist((b) => ({ ...b, ...d.basicChecklist }));
      if (d.quickEquipment)  setQuickEquipment((q) => ({ ...q, ...d.quickEquipment }));
      if (d.invoiceForm)     setInvoiceForm((f) => ({ ...f, ...d.invoiceForm }));
      if (d.paymentForm)     setPaymentForm((f) => ({ ...f, ...d.paymentForm }));
      if (d.hspForm)         setHspForm((f) => ({ ...f, ...d.hspForm }));
      if (d.officeBilling)   setOfficeBilling((f) => ({ ...f, ...d.officeBilling }));
      if (d.estimateForm)    setEstimateForm((f) => ({ ...f, ...d.estimateForm }));
      if (Array.isArray(d.equipment) && d.equipment.length) setEquipment(d.equipment);
      if (d.billingOutcome && !initialOutcome) setBillingOutcome(d.billingOutcome);
      if (d.nexflowWarrantyReason) setNexflowWarrantyReason(d.nexflowWarrantyReason);
      if (typeof d.sendReviewRequest === 'boolean') setSendReviewRequest(d.sendReviewRequest);
      if (d.visitOutcome)    setVisitOutcome(d.visitOutcome);
      if (d.returnVisit)     setReturnVisit((r) => ({ ...r, ...d.returnVisit }));
    },
  });
  const clearDraft = () => draft.clear();
  const closeDone = () => { clearDraft(); onClose?.(true); };
  const discardDraft = () => {
    if (!window.confirm('Discard everything entered for this closeout?')) return;
    clearDraft();
    toast.success('Draft discarded');
    onClose?.(false);
  };

  // iter112 — Auto-detect existing active membership for this appointment's customer.
  // When the wizard reaches the invoice step we use this to prefill membership_discount_percent
  // to 10% so the tech doesn't have to type it for every member.
  useEffect(() => {
    let cancelled = false;
    const cid = appointment?.customer_id;
    if (!cid) { setCustomerHasActiveMembership(false); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/customers/${cid}/membership-status`, { withCredentials: true });
        if (!cancelled) { setCustomerHasActiveMembership(!!data?.active); setCustomerMembership(data || null); }
      } catch {
        // Endpoint may not exist for all roles; fall back to checking the customer doc.
        try {
          const { data } = await axios.get(`${API}/admin/customers/${cid}`, { withCredentials: true });
          if (!cancelled) setCustomerHasActiveMembership(!!data?.has_active_membership || !!data?.membership_tier);
        } catch { /* tech-role may not have customer read; that's fine, default false */ }
      }
    })();
    return () => { cancelled = true; };
  }, [appointment?.customer_id]);

  const loadTemplate = useCallback(async () => {
    if (!appointment) return;
    try {
      const params = {};
      if (appointment.employee_department) params.department = appointment.employee_department;
      if (appointment.service_type) params.service_type = appointment.service_type;
      const { data } = await axios.get(`${API}/completion-templates/applicable`, { withCredentials: true, params });
      setTemplate(data);
    } catch { setTemplate(null); }
  }, [appointment]);

  // If a completion already exists for this appointment (tech clicked Finish Closeout),
  // jump straight to the unfinished step rather than restarting the form.
  // 911-fix-iter173 — On resume, we ALSO:
  //   (1) Seed `base` + `basicChecklist` + `quickEquipment` from the saved
  //       completion doc so the tech can hit Back and see what they entered.
  //   (2) Pre-populate `stepHistory` with the prior steps so Back works just
  //       like a fresh first run. Without this, hitting Back on the resume
  //       trapped the tech on the invoice step with no escape.
  const resumeIfNeeded = useCallback(async () => {
    if (!appointment?.id) return;
    try {
      const { data } = await axios.get(`${API}/jobs/completion/by-appointment/${appointment.id}`, { withCredentials: true });
      if (!data || !data.id) return;
      // Tech only resumes their own pending completion
      if (data.status !== 'pending_approval' && data.status !== 'revisions_requested') return;
      setCompletionId(data.id);
      // A restored draft is newer than the server seed — never overwrite what the tech typed.
      const rd = restoredDraftRef.current || {};
      const hasLines = (f) => (f?.line_items || []).some((li) => (li.description || '').trim() || Number(li.unit_price));

      // 911-fix-iter173 — Seed previously saved completion fields so Back is useful.
      if (!rd.base) setBase((b) => ({
        ...b,
        work_performed: data.work_performed || '',
        materials_used: data.materials_used || '',
        hours_worked: data.hours_worked != null ? String(data.hours_worked) : '',
        recommended_followups: data.recommended_followups || '',
        customer_signature_name: data.customer_signature_name || appointment?.customer_name || '',
      }));
      if (data.basic_checklist && !rd.basicChecklist) {
        setBasicChecklist((bc) => ({ ...bc, ...data.basic_checklist }));
      }
      if (data.quick_equipment && !rd.quickEquipment) {
        setQuickEquipment((qe) => ({ ...qe, ...data.quick_equipment }));
      }

      if (!data.invoice_status) {
        // iter258 — Invoice Decision step removed. A completion with no billing
        // outcome recorded reopens Page 1 so the tech re-picks the Billing
        // Outcome — the old decision picker must never resurface.
        setStepHistory([]);
        setBillingOutcome(data.billing_outcome || '');
        setStep('form');
      } else if (['hsp_warranty_claim', 'nexflow_warranty', 'return_visit_needed', 'customer_not_available', 'save_draft', 'deferred_payment_approved',
                  'deferred_unable_collect',
                  'warranty', 'hsp', 'return_visit'].includes(data.invoice_status)) {
        // Already closed — surface and close
        toast.success('This job is already closed out.');
        closeDone();
      } else if (data.invoice_status === 'office_review_required' && !data.office_review_draft_id) {
        // Office Billing Submission not sent yet — reopen it (restored draft fields stay).
        setBillingOutcome('office_review_required');
        if (!(rd.officeBilling?.invoice_description || '').trim()) {
          setOfficeBilling((o) => ({ ...o, invoice_description: (data.work_performed || '').slice(0, 200) }));
        }
        setStepHistory(['form']);
        setStep('office_billing');
      } else if (data.invoice_status === 'estimate_only' && !data.estimate_id) {
        // Estimate-Only submitted but estimate never saved — resume the builder.
        if (!hasLines(rd.estimateForm) && !(rd.estimateForm?.scope || '').trim()) setEstimateForm((f) => ({
          ...f,
          title: `Estimate — ${appointment.service_type || appointment.shift_title || 'Service'}`,
          scope: data.work_performed || '',
          line_items: [{ description: (data.work_performed || '').slice(0, 100), quantity: 1, unit_price: 0, kind: 'service' }],
        }));
        setStepHistory(['form']);
        setStep('estimate');
      } else if ((data.invoice_status === 'create_invoice' || data.invoice_status === 'send_invoice' || data.invoice_status === 'hsp_claim') && !data.invoice_id) {
        if (!hasLines(rd.invoiceForm)) setInvoiceForm({
          amount: '',
          line_items: [{ description: (data.work_performed || '').slice(0, 80), quantity: 1, unit_price: 0 }],
          discount: 0, membership_savings: 0, notes: '',
          save_as_draft: false,
        });
        // iter258 — Invoice Decision step removed. Back from the resumed invoice
        // step walks straight back to Page 1 (form).
        setStepHistory(['form']);
        setStep('invoice');
      } else if (data.invoice_id && !data.payment_disposition) {
        setPaymentForm((p) => (rd.paymentForm?.amount ? p : { ...p, amount: String((data.invoice_amount || 0).toFixed(2)) }));
        setStepHistory(['form', 'invoice']);
        setStep('payment');
      }
    } catch { /* ignore — start at form */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment, onClose]);

  // Run once per Visit — a refreshed appointment object (auto-refresh, clock/auth revalidation)
  // must never re-seed and wipe in-progress invoice/estimate entry.
  const resumedForRef = useRef(null);
  useEffect(() => {
    if (!appointment?.id || resumedForRef.current === appointment.id) return;
    resumedForRef.current = appointment.id;
    loadTemplate(); resumeIfNeeded();
  }, [appointment?.id, loadTemplate, resumeIfNeeded]);

  // iter73 — Lazy-load Warranty Company customer records the first time the tech lands on the
  // HSP/Warranty details step. Cached for the lifetime of the modal.
  useEffect(() => {
    if (step !== 'hsp_details' || warrantyCompanies.length > 0 || loadingWarrantyCos) return;
    setLoadingWarrantyCos(true);
    axios.get(`${API}/customers`, {
      params: { customer_type: 'warranty_company' },
      withCredentials: true,
    })
      .then(({ data }) => {
        const rows = (data || []).map((c) => ({
          id: c.id,
          name: c.company_name || c.name || 'Unnamed Warranty Company',
        })).sort((a, b) => a.name.localeCompare(b.name));
        setWarrantyCompanies(rows);
      })
      .catch(() => setWarrantyCompanies([]))
      .finally(() => setLoadingWarrantyCos(false));
  }, [step, warrantyCompanies.length, loadingWarrantyCos]);

  // iter113 — When the tech opens Stripe Checkout via "Customer Paying Card Now",
  // poll the invoice paid status every 3s so the wizard can auto-advance the moment
  // the Stripe webhook confirms payment. Polling stops on paid, on wizard close, or
  // when the user switches to a different disposition.
  // NOTE: Must be declared BEFORE the `if (!appointment) return null` early-return
  // below — React rules-of-hooks require hooks to run in the same order on every render.
  useEffect(() => {
    if (!cardNow.sessionId || cardNow.paid || paymentForm.disposition !== 'paying_card_now') return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/jobs/completion/${completionId}/card-now-status`, { withCredentials: true });
        if (cancelled) return;
        if (data.paid) {
          setCardNow((c) => ({ ...c, paid: true, polling: false }));
          // Auto-fill paymentForm with the confirmed values so the closeout summary lights up.
          setPaymentForm((p) => ({
            ...p,
            disposition: 'paid_card',  // Stripe-confirmed → standard paid_card record
            amount: String((data.amount_paid || 0).toFixed(2)),
            reference: cardNow.sessionId.slice(-12),
            notes: 'Stripe Checkout confirmed',
          }));
          toast.success('Stripe confirmed — payment received');
        }
      } catch { /* offline / 5xx — keep polling */ }
    };
    const id = setInterval(tick, 3000);
    setCardNow((c) => ({ ...c, polling: true }));
    return () => { cancelled = true; clearInterval(id); };
  }, [cardNow.sessionId, cardNow.paid, paymentForm.disposition, completionId]);

  // iter122 — Auto-default to Warranty Invoice when the master schedule shift has
  // a Work Order # populated. The tech can still tap Back and pick something else
  // — we just save them a step on the common case. MUST live before the
  // `if (!appointment) return null` early-return below per rules-of-hooks.
  useEffect(() => {
    if (step !== 'decision') return;
    if (autoPickedWarrantyRef.current) return;
    if (!dispatcherWO) return;
    const hsp = INVOICE_DECISIONS.find((d) => d.key === 'hsp_claim');
    if (!hsp) return;
    autoPickedWarrantyRef.current = true;
    pickDecisionRef.current && pickDecisionRef.current(hsp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, dispatcherWO]);

  if (!appointment) return null;

  const setAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));
  const toggleFlag = (key) => setOpportunityFlags((arr) => arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key]);

  const addEquipment = () => setEquipment((e) => [...e, newAsset()]);
  const removeEquipment = (idx) => setEquipment((e) => e.filter((_, i) => i !== idx));
  const patchEquipment = (idx, patch) => setEquipment((e) => e.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

  const submitReturnVisit = async (payload) => {
    try {
      const { data } = await axios.post(`${API}/jobs/${appointment.id}/complete`, payload, { withCredentials: true });
      setCompletionId(data.id);
      toast.success('Visit finished — office will schedule the return visit. Job stays open.');
      closeDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    }
    setSubmitting(false);
  };

  const submit = async () => {
    if (!base.work_performed.trim()) { toast.error('Tell us what you did'); return; }
    const isReturn = visitOutcome === 'return_visit';
    if (isReturn && returnVisitError(returnVisit)) { toast.error(returnVisitError(returnVisit)); return; }
    // iter184 — HARD gate: no completion may leave Page 1 without a billing
    // outcome. Tech unsure picks "Office Billing Review Required".
    if (!isReturn && !billingOutcome) {
      toast.error('Pick a Billing Outcome before completing the job.');
      return;
    }
    // Phase 1b — block completion when a captured asset is missing a required
    // identifier (e.g. Model/Serial for equipment we install) unless the tech
    // explicitly marked "Unable to Capture" with a reason. Never silently pass.
    if (assetError) {
      toast.error(assetError);
      return;
    }
    setSubmitting(true);
    const payload = {
      ...base,
      hours_worked: base.hours_worked === '' ? null : Number(base.hours_worked),
      // iter82 bugfix — when the slim Page 1 is in effect (SHOW_FULL_INSPECTION=false), do NOT
      // send template_id. The hidden template questions ("Customer satisfied?" etc.) would still
      // trip the backend's required-answer validation and block submission with
      // "Missing required answer: Customer satisfied?". The new Basic Checklist + Quick
      // Equipment Notes replace that data capture for standard service calls.
      template_id: SHOW_FULL_INSPECTION ? template?.id : null,
      answers: SHOW_FULL_INSPECTION ? answers : {},
      opportunity_flags: SHOW_FULL_INSPECTION ? opportunityFlags : [],
      send_review_request: !!sendReviewRequest && visitOutcome !== 'return_visit',
      // iter74 — slim Page 1 captures
      basic_checklist: basicChecklist,
      quick_equipment: quickEquipment,
      // iter184 — Billing outcome (mandatory, backend-gated).
      billing_outcome: isReturn ? 'return_visit_needed' : billingOutcome,
      visit_outcome: isReturn ? 'return_visit' : 'job_complete',
      return_visit: isReturn ? returnVisitPayload(returnVisit) : null,
      // iter304 — optional no-charge reason category (only meaningful for nexflow_warranty).
      nexflow_warranty_reason: billingOutcome === 'nexflow_warranty' ? (nexflowWarrantyReason || null) : null,
      equipment: equipment.map((e) => ({
        scope: 'residential',
        customer_id: appointment.customer_id,
        asset_type_key: e.asset_type_key || e.type,
        type: e.asset_type_key || e.type,
        work_performed: e.work_performed || 'installed_new',
        existing_asset_id: e.existing_asset_id || null,
        brand: e.brand, model: e.model, serial: e.serial,
        location: e.location,
        install_date: e.install_date || null,
        warranty_until: e.warranty_until || null,
        warranty: e.warranty || null,
        custom_fields: e.custom_fields || {},
        unable_to_capture: e.unable_to_capture || null,
        condition: e.condition, notes: e.notes,
        recommended_replacement_date: e.recommended_replacement_date || null,
        media_ids: e.media_ids || [],
        photo_urls: e.photo_urls || [],
        source_appointment_id: appointment.id,
        equipment_source: e.equipment_source || null,
        quantity: e.quantity || 1,
        inventory_item_id: e.inventory_item_id || null,
        supplier_name: e.supplier_name || null,
      })),
    };
    if (isReturn) { await submitReturnVisit(payload); return; }
    try {
      const { data } = await axios.post(`${API}/jobs/${appointment.id}/complete`, payload, { withCredentials: true });
      if (data.duplicate_warnings && data.duplicate_warnings.length > 0) {
        toast.warning(`${data.duplicate_warnings.length} serial(s) flagged as possible duplicates — manager will review.`);
      }
      setCompletionId(data.id);
      // Draft kept through invoice/estimate/payment steps; cleared only when the whole
      // closeout finishes (closeDone) or is explicitly discarded.
      // iter205 — "Office to Finalize & Submit Invoice" routes to a dedicated Office
      // Billing Submission screen (NOT a blank no-invoice close). The tech supplies the
      // extra billing info the office needs; a real office_review invoice draft is created.
      if (billingOutcome === 'office_review_required') {
        setStepHistory(['form']);
        setOfficeBilling((o) => ({
          ...o,
          invoice_description: base.work_performed?.slice(0, 200) || '',
        }));
        goToStep('office_billing');
        setSubmitting(false);
        return;
      }
      // Estimate-Only routes to a real Create Estimate builder (NOT a dead-end close).
      // The job is only completed once the estimate saves successfully.
      if (billingOutcome === 'estimate_only') {
        setStepHistory(['form']);
        setEstimateForm((f) => ({
          ...f,
          title: `Estimate — ${appointment.service_type || appointment.shift_title || 'Service'}`,
          scope: base.work_performed || '',
          line_items: [{ description: (base.work_performed || '').slice(0, 100), quantity: 1, unit_price: 0, kind: 'service' }],
        }));
        goToStep('estimate');
        setSubmitting(false);
        return;
      }
      // iter184 — Auto-route to the correct next step based on the Page 1
      // billing outcome. Full INVOICE_DECISIONS handling stays intact for
      // paths that need HSP details / invoice creation / payment collection.
      const decision = INVOICE_DECISIONS.find((d) => d.key === billingOutcome);
      if (decision) {
        // Reuse the existing decision-picker path so all downstream side
        // effects (invoice mirror, HSP flow, payment step) still fire.
        // Thread the freshly-returned completion id explicitly — React state
        // (setCompletionId above) is not yet committed within this same handler,
        // so reading `completionId` here would post to /completion/null/...
        setStepHistory(['form']);
        await pickDecision(decision, data.id);
      } else {
        // iter258 — Invoice Decision step removed. Fallback (should never hit —
        // every BILLING_OUTCOMES key maps to an INVOICE_DECISIONS entry) simply
        // reopens Page 1 so the tech can re-pick a Billing Outcome. It must never
        // surface the old decision picker.
        setStepHistory([]);
        setStep('form');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    }
    setSubmitting(false);
  };

  const pickDecision = async (d, cidArg) => {
    setPickedDecision(d);
    // iter69 — HSP requires capturing payor metadata before we hit the server. Detour to hsp_details
    // and confirm there.
    if (d.needsHspDetails) {
      goToStep('hsp_details');
      return;
    }
    await commitDecisionToServer(d, null, cidArg);
  };
  // iter122 — keep the forward-ref in sync so the auto-pick effect (declared
  // before the early-return up top) can call the latest pickDecision.
  pickDecisionRef.current = pickDecision;

  const commitDecisionToServer = async (d, hsp, cidArg) => {
    setSubmitting(true);
    // Prefer the explicitly-threaded id (fresh completion) and fall back to state
    // for the later manual paths (hsp confirm) where state is already committed.
    const cid = cidArg || completionId;
    // Hard guard — never allow a request to hit /jobs/completion/null/... If the
    // completion id is somehow missing, keep the tech on the billing step with
    // their data intact and let them retry rather than firing a bad write.
    if (!cid) {
      toast.error('Completion not saved yet — please tap Submit again.');
      setSubmitting(false);
      return;
    }
    try {
      const payload = { invoice_status: d.key };
      if (hsp) {
        payload.hsp_provider = hsp.provider;
        payload.hsp_work_order_number = hsp.work_order_number;
        // iter302 — structured payer split (net warranty computed server-side).
        payload.approved_amount = Number(hsp.approved_amount || 0);
        payload.deductible = Number(hsp.deductible || 0);
        payload.uncovered_balance = Number(hsp.uncovered_balance || 0);
        payload.hsp_notes = hsp.notes || '';
        // iter73 — point at the existing Warranty Company customer when chosen
        if (hsp.warranty_customer_id) payload.warranty_customer_id = hsp.warranty_customer_id;
      }
      const { data } = await axios.post(`${API}/jobs/completion/${cid}/invoice-decision`,
        payload, { withCredentials: true });
      if (data.needs_invoice_creation) {
        // iter302 — keep the server-computed allocation for the reconciliation panel.
        if (d.key === 'hsp_claim') setHspAllocation(data.warranty_allocation || null);
        else setHspAllocation(null);
        // iter156 — Membership Included default lines (editable). Each starts at $0
        // unit price; the customer is never charged for membership-covered work.
        if (d.key === 'membership_included') {
          setInvoiceForm({
            amount: '0.00',
            line_items: MEMBERSHIP_INCLUDED_DEFAULT_LINES.map((desc) => ({
              description: desc,
              quantity: 1,
              unit_price: 0,
              kind: 'service',
            })),
            discount: 0, membership_savings: 0, membership_discount_percent: 0,
            notes: 'Covered under active NexFlow membership.',
            save_as_draft: false,
          });
        } else if (d.key === 'hsp_claim' && hsp) {
          // iter302 — Seed the CUSTOMER invoice with the customer-owed portions of the
          // covered scope: deductible + uncovered balance. Both are NON-discountable
          // (a deductible is never member-discounted). The tech then ADDS any
          // additional customer-only work as normal (discount-eligible) service lines.
          const ded = Number(hsp.deductible || 0);
          const unc = Number(hsp.uncovered_balance || 0);
          const wo = hsp.work_order_number || '';
          const seeded = [];
          if (ded > 0) seeded.push({ description: `Deductible / Service Fee (Warranty claim# ${wo})`, quantity: 1, unit_price: ded, kind: 'service', discountable_override: false });
          if (unc > 0) seeded.push({ description: `Uncovered balance on covered work (claim# ${wo})`, quantity: 1, unit_price: unc, kind: 'service', discountable_override: false });
          if (seeded.length === 0) seeded.push({ description: 'Additional customer-only work', quantity: 1, unit_price: 0, kind: 'service' });
          setInvoiceForm({
            amount: '',
            line_items: seeded,
            discount: 0, membership_savings: 0, membership_discount_percent: 0,
            notes: '', save_as_draft: false,
          });
        } else {
          setInvoiceForm({
            amount: '',
            line_items: [{
              description: base.work_performed.slice(0, 80),
              quantity: 1,
              unit_price: 0,
            }],
            discount: 0, membership_savings: 0, notes: '',
            save_as_draft: d.key === 'save_draft',
          });
        }
        goToStep('invoice');
      } else if (data.needs_payment_disposition) {
        goToStep('payment');
      } else {
        // nexflow_warranty / return_visit_needed / customer_not_available — fully closed
        toast.success(`Job closed: ${d.label}`);
        closeDone();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setSubmitting(false);
  };

  const submitHspDetails = async () => {
    if (!hspForm.provider.trim()) { toast.error('Warranty Company / Program is required'); return; }
    if (!hspForm.work_order_number.trim()) { toast.error('Warranty Claim / Work Order Number is required'); return; }
    const approved = Number(hspForm.approved_amount || 0);
    const ded = Number(hspForm.deductible || 0);
    const unc = Number(hspForm.uncovered_balance || 0);
    if (hspForm.approved_amount === '' || approved < 0) {
      toast.error('HSP/Warranty Approved / Authorized Amount is required'); return;
    }
    if (ded < 0 || unc < 0) { toast.error('Amounts cannot be negative'); return; }
    if (ded > approved) {
      toast.error(`Customer deductible ($${ded.toFixed(2)}) cannot exceed the approved amount ($${approved.toFixed(2)}).`); return;
    }
    await commitDecisionToServer(pickedDecision, hspForm);
  };

  const createInvoice = async () => {
    if (!completionId) {
      toast.error('Completion not saved yet — please tap Submit again.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...invoiceForm };
      payload.amount = invoiceForm.amount === '' || invoiceForm.amount === null ? null : Number(invoiceForm.amount);
      payload.discount = Number(invoiceForm.discount || 0);
      // iter112 — Send the discount percentage; backend computes the dollar amount
      // so the math is always server-authoritative.
      payload.membership_discount_percent = Number(invoiceForm.membership_discount_percent || 0);
      // Normalize line items so every one carries a `kind`. iter298 — preserve the
      // structured membership plan identity produced by the picker (plan_key/name/
      // cadence/complimentary) so activation is plan-aware, not description-guessed.
      payload.line_items = (invoiceForm.line_items || []).map((li) => ({
        description: li.description || '',
        quantity: Number(li.quantity || 1),
        unit_price: Number(li.unit_price || 0),
        kind: (li.kind || 'service'),
        ...(li.kind === 'membership' ? {
          membership_plan_key: li.membership_plan_key || null,
          plan_name: li.plan_name || null,
          billing_cadence: li.billing_cadence || null,
          complimentary: !!li.complimentary,
        } : {}),
      }));
      // iter298 — An explicit COMPLIMENTARY membership creates a $0 invoice on purpose;
      // auto-supply an approved zero-charge reason so the zero-dollar guard allows it
      // (a random $0 invoice from credits/warranty still requires a manual reason).
      const onlyComplimentaryMembership = payload.line_items.length > 0
        && payload.line_items.every((li) => li.kind === 'membership' && li.complimentary)
        && Number(payload.amount || 0) === 0;
      if (onlyComplimentaryMembership && !payload.zero_reason) {
        payload.zero_reason = 'complimentary_membership';
      }
      // iter302 — Warranty-only ($0 customer): on an HSP/warranty job where the
      // customer owes nothing (no deductible/uncovered/extra work), don't create a
      // meaningless $0 customer invoice. Signal the backend to create ONLY the
      // warranty invoice while preserving the full payer allocation.
      const isHspClaim = pickedDecision?.key === 'hsp_claim';
      const customerTotalNow = (payload.line_items || [])
        .reduce((s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0), 0);
      const overrideAmt = payload.amount && payload.amount > 0 ? payload.amount : 0;
      const custOwes = overrideAmt || customerTotalNow;
      const netWarranty = Number(hspAllocation?.net_warranty_amount || 0);
      if (isHspClaim && Math.round(custOwes * 100) === 0 && netWarranty > 0) {
        payload.warranty_only = true;
      }
      const { data } = await axios.post(`${API}/jobs/completion/${completionId}/invoice`, payload, { withCredentials: true });
      // iter302 — warranty-only: no customer invoice, no payment step.
      if (data.warranty_only) {
        toast.success(`Warranty claim recorded — customer owes $0.00. Warranty company: $${netWarranty.toFixed(2)}.`);
        closeDone();
        setSubmitting(false);
        return;
      }
      setInvoiceId(data.id);
      clearDraft();   // invoice submitted — draft no longer needed
      setPaymentForm((p) => ({ ...p, amount: String((data.amount || 0).toFixed(2)) }));
      // iter114 — Remember if a membership line was on this invoice so we can prompt
      // the asset-log step AFTER the job closes. Tech-friendly: zero extra clicks if
      // no membership was sold.
      setInvoiceHadMembership(!!(payload.line_items || []).some((li) => li.kind === 'membership'));
      toast.success(`Invoice $${data.amount.toFixed(2)} ${data.status === 'draft' ? 'saved as draft' : 'created'}`);
      if (pickedDecision?.key === 'save_draft' || pickedDecision?.key === 'deferred_payment_approved') {
        closeDone();
      } else if (pickedDecision?.key === 'membership_included') {
        // iter156 — $0 membership-included invoice. No payment step. Close the wizard
        // with success; customer portal + history now show the membership service.
        toast.success('Membership service recorded — $0 due, covered under membership');
        closeDone();
      } else {
        goToStep('payment');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invoice failed');
    }
    setSubmitting(false);
  };

  const submitOfficeBilling = async () => {
    const basis = officeBilling.amount_basis;
    if (!basis) { toast.error('Choose how the amount should be handled.'); return; }
    if ((basis === 'final_known' || basis === 'previously_quoted')) {
      if (officeBilling.proposed_amount === '' || Number(officeBilling.proposed_amount) <= 0) {
        toast.error('Enter the invoice amount.'); return;
      }
    }
    setSubmitting(true);
    try {
      const body = {
        amount_basis: basis,
        proposed_amount: officeBilling.proposed_amount === '' ? null : Number(officeBilling.proposed_amount),
        amount_already_approved: officeBilling.amount_already_approved === '' ? null : Number(officeBilling.amount_already_approved),
        estimate_id: officeBilling.estimate_id || null,
        invoice_description: officeBilling.invoice_description || '',
        labor_notes: officeBilling.labor_notes || '',
        labor_hours: officeBilling.labor_hours === '' ? null : Number(officeBilling.labor_hours),
        materials: officeBilling.materials || '',
        equipment_notes: officeBilling.equipment_notes || '',
        payment_terms: officeBilling.payment_terms || '',
        reason_not_collected: officeBilling.reason_not_collected || '',
        po_number: officeBilling.po_number || '',
        billing_notes: officeBilling.billing_notes || '',
        additional_work: !!officeBilling.additional_work,
        additional_work_notes: officeBilling.additional_work_notes || '',
        customer_approval_name: officeBilling.customer_approval_name || '',
      };
      const { data } = await axios.post(`${API}/jobs/completion/${completionId}/office-billing-submit`, body, { withCredentials: true });
      setOfficeSubmitResult(data);
      clearDraft();   // submitted — draft no longer needed
      goToStep('office_submitted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit to office');
    }
    setSubmitting(false);
  };

  const addEstimateLine = () => setEstimateForm((f) => ({
    ...f, line_items: [...f.line_items, { description: '', quantity: 1, unit_price: 0, kind: 'service' }],
  }));
  const updateEstimateLine = (i, key, val) => setEstimateForm((f) => ({
    ...f, line_items: f.line_items.map((li, idx) => idx === i ? { ...li, [key]: val } : li),
  }));
  const removeEstimateLine = (i) => setEstimateForm((f) => ({
    ...f, line_items: f.line_items.filter((_, idx) => idx !== i),
  }));
  const estimateTotal = () => {
    const sub = (estimateForm.line_items || []).reduce((s, li) => s + (Number(li.quantity || 0) * Number(li.unit_price || 0)), 0);
    return Math.max(0, sub - Number(estimateForm.discount || 0));
  };

  const submitEstimate = async () => {
    const lines = (estimateForm.line_items || []).filter((li) => (li.description || '').trim() || Number(li.unit_price));
    if (lines.length === 0) { toast.error('Add at least one line item with a description or price.'); return; }
    if (!completionId) { toast.error('Completion not saved yet — please retry.'); return; }
    setSubmitting(true);
    try {
      const body = {
        title: estimateForm.title || '',
        scope: estimateForm.scope || '',
        line_items: lines.map((li) => ({
          description: li.description || '', quantity: Number(li.quantity || 1),
          unit_price: Number(li.unit_price || 0), kind: li.kind || 'service',
        })),
        customer_notes: estimateForm.customer_notes || '',
        internal_notes: estimateForm.internal_notes || '',
        expiration_date: estimateForm.expiration_date || null,
        follow_up_date: estimateForm.follow_up_date || null,
        discount: Number(estimateForm.discount || 0),
      };
      const { data } = await axios.post(`${API}/jobs/completion/${completionId}/create-estimate`, body, { withCredentials: true });
      setEstimateResult(data);
      clearDraft();   // submitted — draft no longer needed
      toast.success(`Estimate ${data.estimate_number} created`);
      goToStep('estimate_created');
    } catch (e) {
      // Do NOT close the wizard or clear entered data — allow retry.
      toast.error(e.response?.data?.detail || 'Could not create estimate — please retry.');
    }
    setSubmitting(false);
  };

  const startStripeCardNow = async () => {
    if (cardNow.opening) return;
    setCardNow((c) => ({ ...c, opening: true }));
    try {
      const { data } = await axios.post(`${API}/jobs/completion/${completionId}/stripe-card-now`, {}, { withCredentials: true });
      // Open Stripe Checkout in a new tab. On phones this triggers the in-app Safari/Chrome
      // sheet which is exactly what we want — the customer can pay on the tech's device.
      window.open(data.url, '_blank', 'noopener');
      setCardNow({ url: data.url, sessionId: data.session_id, paid: false, polling: true, opening: false });
      toast.success('Stripe Checkout opened in a new tab — hand the phone to the customer');
    } catch (e) {
      setCardNow((c) => ({ ...c, opening: false }));
      toast.error(e.response?.data?.detail || 'Could not start Stripe Checkout');
    }
  };

  const recordPayment = async () => {
    // iter113 — Block premature close: 'paying_card_now' is settled by Stripe only.
    // The tech must wait until the polling marks paid=true (which flips the
    // disposition to 'paid_card' automatically). If they try too early, redirect them.
    if (paymentForm.disposition === 'paying_card_now') {
      if (!cardNow.sessionId) { toast.error('Tap "Open Stripe Checkout" first — customer needs to pay'); return; }
      if (!cardNow.paid)      { toast.error('Waiting for Stripe to confirm payment — please don\'t close yet'); return; }
    }
    // iter113 — "Deferred / Unable to Collect Today" requires a reason in
    // payment_method_detail. Backend re-validates with explicit copy.
    if (paymentForm.disposition === 'deferred_unable_collect' && !paymentForm.payment_method_detail) {
      toast.error('Pick a reason — Customer not available, Manager approved, Waiting on approval, Payment link will be sent, or Other'); return;
    }
    // iter112 — Customer Paid — Other requires payment_method_detail AND reference.
    // Frontend blocks early; backend re-validates so we never close a vague payment.
    if (paymentForm.disposition === 'paid_other') {
      if (!paymentForm.payment_method_detail) { toast.error('Pick a payment method detail (QuickBooks Pay, Zelle, or Already Paid)'); return; }
      if (!(paymentForm.reference || '').trim()) { toast.error('Add a reference (confirmation number / last 4 / note)'); return; }
      if (!(Number(paymentForm.amount) > 0)) { toast.error('Enter the amount collected (must be greater than $0)'); return; }
      if (!(paymentForm.notes || '').trim()) { toast.error('Add notes for the "Paid — Other" payment'); return; }
    }
    // iter231/iter240 — Cash/Check/Card/QuickBooks require a positive on-site amount.
    if (['paid_cash', 'paid_check', 'paid_card', 'paid_quickbooks'].includes(paymentForm.disposition) && !(Number(paymentForm.amount) > 0)) {
      toast.error('Enter the amount collected on site (must be greater than $0)'); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        disposition: paymentForm.disposition,
        amount: paymentForm.amount === '' ? null : Number(paymentForm.amount),
        reference: paymentForm.reference,
        notes: paymentForm.notes,
        payment_method_detail: paymentForm.payment_method_detail || '',
      };
      await axios.post(`${API}/jobs/completion/${completionId}/payment-disposition`, payload, { withCredentials: true });
      toast.success('Job fully closed — revenue recorded');
      // iter114 — Membership asset-log gate. Only triggers if a membership line was
      // actually on this invoice. Otherwise close the wizard exactly like before.
      if (invoiceHadMembership) {
        setShowMembershipPrompt(true);
      } else {
        closeDone();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setSubmitting(false);
  };

  // iter119 — Membership Onboarding decision API. Tech picks one of three
  // paths; backend persists the status onto the customer record AND drops the
  // appropriate draft into the Unscheduled rail (for schedule + office_followup).
  const submitOnboardingDecision = async (decision) => {
    setAssetSubmitting(true);
    try {
      const body = {
        decision,
        include_water_heater_flush: decision === 'schedule' ? !!includeWHFlush : false,
        preferred_date: decision === 'schedule' ? (onboardingPreferredDate || null) : null,
        notes: (onboardingNotes || '').trim() || null,
      };
      const { data } = await axios.post(
        `${API}/jobs/completion/${completionId}/membership-onboarding`,
        body,
        { withCredentials: true },
      );
      const friendly = {
        schedule: 'Onboarding inspection scheduled — added to the Unscheduled rail',
        quick:    'Quick setup recorded — finish the Plumbing Profile when you have time',
        office_followup: 'Office follow-up created — admin will call the customer',
      }[decision] || 'Onboarding recorded';
      toast.success(friendly);
      if (data?.draft_id) {
        toast.message(`Draft #${data.draft_id.slice(-6)} created`);
      }
      // iter119 — Reset overlay state so re-opening the wizard for the next job
      // doesn't carry stale values into the next onboarding decision.
      setMembershipAssetMode(null);
      setIncludeWHFlush(true);
      setOnboardingPreferredDate('');
      setOnboardingNotes('');
      setShowMembershipPrompt(false);
      closeDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record onboarding decision');
    }
    setAssetSubmitting(false);
  };

  const renderQuestion = (q, cat) => {
    const v = answers[q.id];
    if (q.type === 'opportunity_flag') {
      const active = opportunityFlags.includes(q.opportunity_key);
      return (
        <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
          <input type="checkbox" checked={active}
            onChange={() => toggleFlag(q.opportunity_key)}
            data-testid={`wiz-q-${q.id}`}
            className="accent-[#D4AF37]" />
          <span>{q.label}{q.required && <span className="text-red-400 ml-1">*</span>}</span>
        </label>
      );
    }
    if (q.type === 'equipment_block') {
      return <EquipmentBlock equipment={equipment} addEquipment={addEquipment} removeEquipment={removeEquipment} patchEquipment={patchEquipment} />;
    }
    if (q.type === 'checkbox') {
      return (
        <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
          <input type="checkbox" checked={!!v} onChange={(e) => setAnswer(q.id, e.target.checked)}
            data-testid={`wiz-q-${q.id}`} className="accent-[#D4AF37]" />
          <span>{q.label}{q.required && <span className="text-red-400 ml-1">*</span>}</span>
        </label>
      );
    }
    if (q.type === 'yes_no') {
      return (
        <div className="space-y-1">
          <p className="text-sm text-white">{q.label}{q.required && <span className="text-red-400 ml-1">*</span>}</p>
          <div className="flex gap-2">
            {['yes', 'no'].map((opt) => (
              <button key={opt} type="button" data-testid={`wiz-q-${q.id}-${opt}`}
                onClick={() => setAnswer(q.id, opt)}
                className={`px-3 py-1 rounded text-xs font-medium border ${v === opt ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]' : 'bg-[#0A0A0A] border-[#262626] text-white'}`}>
                {opt.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (q.type === 'text') {
      return (
        <div className="space-y-1">
          <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">{q.label}{q.required && <span className="text-red-400 ml-1">*</span>}</Label>
          <Input value={v || ''} onChange={(e) => setAnswer(q.id, e.target.value)} data-testid={`wiz-q-${q.id}`} className="bg-[#0A0A0A] border-[#262626] text-white" />
        </div>
      );
    }
    if (q.type === 'textarea') {
      return (
        <div className="space-y-1">
          <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">{q.label}{q.required && <span className="text-red-400 ml-1">*</span>}</Label>
          <Textarea value={v || ''} onChange={(e) => setAnswer(q.id, e.target.value)} rows={3} data-testid={`wiz-q-${q.id}`} className="bg-[#0A0A0A] border-[#262626] text-white" />
        </div>
      );
    }
    if (q.type === 'select') {
      return (
        <div className="space-y-1">
          <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">{q.label}{q.required && <span className="text-red-400 ml-1">*</span>}</Label>
          <Select value={v || ''} onValueChange={(val) => setAnswer(q.id, val)}>
            <SelectTrigger data-testid={`wiz-q-${q.id}`} className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Choose..." /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              {(q.options || []).map((o) => <SelectItem key={o} value={o} className="text-white">{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    }
    return null;
  };

  return (
    <Sheet open={!!appointment} onOpenChange={(o) => { if (!o) onClose && onClose(false); }}>
      <SheetContent className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-2xl overflow-y-auto" data-testid="completion-wizard">
        <SheetHeader>
          <SheetTitle className="text-white">
            {step === 'form' && 'Complete Job — '}
            {step === 'invoice' && 'Create Invoice — '}
            {step === 'payment' && 'Payment Disposition — '}
            {step === 'office_billing' && 'Office Billing Submission — '}
            {step === 'office_submitted' && 'Submitted to Office — '}
            {step === 'estimate' && 'Create Estimate — '}
            {step === 'estimate_created' && 'Estimate Submitted — '}
            {appointment.shift_title || appointment.service_type || 'Job'}
          </SheetTitle>
          {/* iter112 — Radix a11y: SheetDescription provides aria-describedby so screen readers
              announce the active step. Visually hidden via sr-only. */}
          <SheetDescription className="sr-only">
            Step {step}. Complete the job, decide on the customer invoice, then record payment.
          </SheetDescription>
        </SheetHeader>
        <DraftStatus status={draft.status} restoredAt={draft.restoredAt} onDiscard={discardDraft} testIdPrefix="wiz-draft" />
        <StepDots step={step} />

        {step === 'form' && (
          <>
            <p className="text-[#A3A3A3] text-sm mt-2">
              Quick service call closeout — your manager will review before publishing. A deeper inspection lives in the customer profile when needed.
            </p>
            <div className="space-y-6 mt-6">
              <div className="space-y-3 bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Work Performed *</Label>
                  <Textarea data-testid="wiz-work" value={base.work_performed}
                    onChange={(e) => setBase({ ...base, work_performed: e.target.value })}
                    rows={3} placeholder="What did you do?" className="bg-[#141414] border-[#262626] text-white mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Customer PO Number</Label>
                  <Input data-testid="wiz-customer-po" value={base.customer_po_number}
                    onChange={(e) => setBase({ ...base, customer_po_number: e.target.value })}
                    placeholder="PO provided at the job (optional — leave blank if none)"
                    className="bg-[#141414] border-[#262626] text-white mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Hours</Label>
                    <Input data-testid="wiz-hours" type="number" step="0.25" value={base.hours_worked}
                      onChange={(e) => setBase({ ...base, hours_worked: e.target.value })}
                      className="bg-[#141414] border-[#262626] text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Customer Signature (typed)</Label>
                    <Input data-testid="wiz-signature" value={base.customer_signature_name}
                      onChange={(e) => setBase({ ...base, customer_signature_name: e.target.value })}
                      className="bg-[#141414] border-[#262626] text-white mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Materials Used</Label>
                  <Textarea data-testid="wiz-materials" value={base.materials_used}
                    onChange={(e) => setBase({ ...base, materials_used: e.target.value })}
                    rows={2} className="bg-[#141414] border-[#262626] text-white mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Recommended Follow-ups</Label>
                  <Textarea data-testid="wiz-followups" value={base.recommended_followups}
                    onChange={(e) => setBase({ ...base, recommended_followups: e.target.value })}
                    rows={2} className="bg-[#141414] border-[#262626] text-white mt-1" />
                </div>
              </div>

              {/* iter74 — slim Page 1: basic checklist + quick equipment notes.
                  The full template-driven categories (membership inspection / equipment survey /
                  sales-opportunity checklist / aging-equipment flags) are intentionally hidden behind
                  SHOW_FULL_INSPECTION so the standard completion stays fast for daily service calls.
                  Re-enable when the dedicated Customer/Membership Inspection workflow is built. */}

              <div data-testid="wiz-basic-checklist" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-2">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Basic Checklist</p>
                {[
                  { key: 'cleaned_work_area',  label: 'Cleaned work area' },
                  { key: 'tested_repair',      label: 'Tested repair' },
                  { key: 'explained_work',     label: 'Explained work to customer' },
                ].map((item) => (
                  <label key={item.key} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`wiz-checklist-${item.key}`}
                      checked={!!basicChecklist[item.key]}
                      onChange={(e) => setBasicChecklist((c) => ({ ...c, [item.key]: e.target.checked }))}
                      className="accent-[#D4AF37] w-4 h-4"
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
                {/* iter112 — Customer Satisfied tri-state. Yes / No / Not sure-N/A.
                    Drives whether the review-request button is enabled below. */}
                <div data-testid="wiz-customer-satisfied" className="pt-2 border-t border-[#1f1f1f] mt-2">
                  <p className="text-white text-sm font-medium mb-2">Customer satisfied?</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {[
                      { key: 'yes', label: 'Yes',          activeClass: 'bg-green-600 border-green-700 text-white' },
                      { key: 'no',  label: 'No',           activeClass: 'bg-red-600 border-red-700 text-white' },
                      { key: 'na',  label: 'Not sure',     activeClass: 'bg-[#525252] border-[#737373] text-white' },
                    ].map((opt) => {
                      const active = basicChecklist.customer_satisfied === opt.key;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          data-testid={`wiz-satisfied-${opt.key}`}
                          onClick={() => {
                            setBasicChecklist((c) => ({ ...c, customer_satisfied: opt.key }));
                            // Auto-block review request when "No"; auto-reset to allow tech opt-in when "Yes".
                            if (opt.key === 'no') setSendReviewRequest(false);
                          }}
                          className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors min-h-[44px] ${
                            active ? opt.activeClass : 'bg-[#0A0A0A] border-[#262626] text-white hover:border-[#3f3f3f]'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <AssetSystemsStep
                serviceType={appointment?.service_type || appointment?.shift_title || ''}
                appointment={appointment}
                equipment={equipment}
                setEquipment={setEquipment}
                onValidityChange={setAssetError}
              />

              {SHOW_FULL_INSPECTION && (template?.categories || []).map((cat) => (
                <div key={cat.id} data-testid={`wiz-cat-${cat.id}`} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-3">
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">{cat.name}</p>
                  {(cat.questions || []).map((q) => (
                    <div key={q.id} className="space-y-1">
                      {renderQuestion(q, cat)}
                      {q.help_text && <p className="text-[10px] text-[#737373]">{q.help_text}</p>}
                    </div>
                  ))}
                </div>
              ))}

              {/* iter62/iter64 — Job Complete: tech opts in/out of the review-request SMS.
                  Default = Skip. Review requests must be an intentional choice for satisfied customers. */}
              {/* iter316 — Visit Outcome: finish the Job, or finish today's Visit only. */}
              <div data-testid="wiz-visit-outcome" className="space-y-2">
                <p className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Visit Outcome</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { key: 'job_complete', label: 'Job Complete', hint: 'All work for this Job is done — continue to billing.' },
                    { key: 'return_visit', label: 'Return Visit / More Work Needed', hint: "Today's Visit is done. Job stays open — office schedules the return." },
                  ].map((o) => (
                    <button key={o.key} type="button" data-testid={`wiz-visit-outcome-${o.key}`} onClick={() => setVisitOutcome(o.key)}
                      className={`text-left rounded-md border px-3 py-2 min-h-[56px] transition-colors ${visitOutcome === o.key
                        ? (o.key === 'return_visit' ? 'bg-sky-500/15 border-sky-400 text-white' : 'bg-[#D4AF37]/15 border-[#D4AF37] text-white')
                        : 'bg-[#0A0A0A] border-[#262626] text-white hover:border-[#D4AF37]/60'}`}>
                      <div className="text-sm font-semibold">{o.label}</div>
                      <div className="text-[10px] text-[#A3A3A3] mt-0.5 leading-tight">{o.hint}</div>
                    </button>
                  ))}
                </div>
                {visitOutcome === 'return_visit' && <ReturnVisitFields value={returnVisit} onChange={setReturnVisit} />}
              </div>

              {appointment?.customer_id && visitOutcome !== 'return_visit' && (
                <div data-testid="wiz-review-prompt" className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-md p-3 space-y-3">
                  <div>
                    <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Job Complete — Review Request</p>
                    <p className="text-white text-sm mt-1">Would you like to send a review request to this customer?</p>
                    <p className="text-[#A3A3A3] text-xs mt-1">
                      Only opt in for customers who had a positive experience. If sent, the review request goes out after closeout is completed/approved — no artificial delay.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid="wiz-review-skip"
                      onClick={() => setSendReviewRequest(false)}
                      className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                        sendReviewRequest === false
                          ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]'
                          : 'bg-[#0A0A0A] border-[#262626] text-white hover:border-[#D4AF37]/40'
                      }`}
                    >
                      Skip Review Request
                    </button>
                    <button
                      type="button"
                      data-testid="wiz-review-send"
                      onClick={() => setSendReviewRequest(true)}
                      disabled={basicChecklist.customer_satisfied !== 'yes'}
                      title={basicChecklist.customer_satisfied !== 'yes' ? 'Mark "Customer satisfied? = Yes" first' : ''}
                      className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 min-h-[44px] ${
                        sendReviewRequest === true
                          ? 'bg-green-600 border-green-700 text-white'
                          : basicChecklist.customer_satisfied !== 'yes'
                            ? 'bg-[#0A0A0A] border-[#262626] text-[#525252] cursor-not-allowed opacity-60'
                            : 'bg-[#0A0A0A] border-[#262626] text-white hover:border-green-700'
                      }`}
                    >
                      Send Review Request
                    </button>
                  </div>
                  <p data-testid="wiz-review-current-choice" className="text-[10px] text-[#525252]">
                    Current selection: <span className="text-white">{sendReviewRequest ? 'Will send' : 'Will skip'}</span>
                  </p>
                </div>
              )}

              {/* iter184 — Mandatory Billing Outcome picker. Hard-blocks submit
                  if none is chosen. Techs unsure of the outcome pick
                  "Office Billing Review Required" — that keeps the job on the
                  Billing Missing dashboard until office resolves. */}
              {visitOutcome !== 'return_visit' && (
              <div data-testid="wiz-billing-outcome" className="space-y-2 pt-2 border-t border-[#262626]">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">
                    Billing Outcome <span className="text-red-400">*</span>
                  </p>
                  {!billingOutcome && (
                    <span className="text-[10px] text-red-400 uppercase tracking-wider">Required</span>
                  )}
                </div>
                <p className="text-[11px] text-[#A3A3A3]">
                  Pick one to close out this job.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {VISIBLE_BILLING_OUTCOMES.map((o) => {
                    const active = billingOutcome === o.key;
                    return (
                      <button
                        key={o.key}
                        type="button"
                        data-testid={`wiz-billing-outcome-${o.key}`}
                        onClick={() => setBillingOutcome(o.key)}
                        className={`text-left rounded-md border px-3 py-2 transition-colors min-h-[56px] ${
                          active
                            ? 'bg-[#D4AF37]/15 border-[#D4AF37] text-white'
                            : 'bg-[#0A0A0A] border-[#262626] text-white hover:border-[#D4AF37]/60'
                        }`}
                      >
                        <div className="text-sm font-semibold">{o.label}</div>
                        <div className="text-[10px] text-[#A3A3A3] mt-0.5 leading-tight">{o.hint}</div>
                      </button>
                    );
                  })}
                </div>
                {billingOutcome === 'nexflow_warranty' && (
                  <div data-testid="wiz-nexflow-warranty-reason" className="mt-2 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3">
                    <p className="text-xs text-[#D4AF37] font-semibold mb-1">No charge — covered by NexFlow workmanship warranty.</p>
                    <label className="text-[11px] text-[#A3A3A3] block mb-1">Reason (optional)</label>
                    <div className="flex flex-wrap gap-2">
                      {NEXFLOW_WARRANTY_REASONS.map((r) => (
                        <button
                          key={r.value}
                          type="button"
                          data-testid={`wiz-nwr-${r.value}`}
                          onClick={() => setNexflowWarrantyReason(nexflowWarrantyReason === r.value ? '' : r.value)}
                          className={`text-xs rounded-full border px-3 py-1 transition-colors ${
                            nexflowWarrantyReason === r.value
                              ? 'bg-[#D4AF37]/20 border-[#D4AF37] text-white'
                              : 'bg-[#0A0A0A] border-[#262626] text-[#A3A3A3] hover:border-[#D4AF37]/60'
                          }`}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              )}

              {visitOutcome === 'return_visit' ? (
                <Button data-testid="wiz-submit" onClick={submit} disabled={submitting || !!returnVisitError(returnVisit)}
                  className="w-full bg-sky-400 text-[#0A0A0A] hover:bg-sky-300 disabled:opacity-60"
                  title={returnVisitError(returnVisit)}>
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  {submitting ? 'Submitting...' : "Finish Today's Visit — Return Needed"}
                </Button>
              ) : (
              <Button data-testid="wiz-submit" onClick={submit} disabled={submitting || !billingOutcome}
                className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] disabled:opacity-60"
                title={!billingOutcome ? 'Pick a Billing Outcome first' : ''}>
                <ClipboardCheck className="w-4 h-4 mr-2" />
                {submitting ? 'Submitting...' : (billingOutcome ? 'Job Complete — Submit & Continue' : 'Pick a Billing Outcome to Continue')}
              </Button>
              )}
            </div>
          </>
        )}

        {step === 'office_billing' && (
          <div className="space-y-4 mt-6" data-testid="wiz-office-billing">
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
              <p className="text-amber-300 text-sm font-medium">Work is complete — the office will finalize & send the invoice.</p>
              <p className="text-[#A3A3A3] text-xs mt-1">Your work, materials, equipment, photos, customer, tech, service date & work order are already saved. Just add the billing info the office needs.</p>
            </div>

            <div>
              <Label className="text-white text-sm">How should the amount be handled?</Label>
              <div className="grid gap-2 mt-2">
                {[
                  ['final_known', 'Final amount known', 'You know the completed invoice total.'],
                  ['previously_quoted', 'Previously quoted amount', 'The job had an approved quote / estimate.'],
                  ['office_to_determine', 'Office to determine amount', 'Completed work needs office pricing or review.'],
                ].map(([k, label, hint]) => (
                  <button key={k} type="button" data-testid={`office-basis-${k}`}
                    onClick={() => setOfficeBilling((o) => ({ ...o, amount_basis: k }))}
                    className={`text-left rounded-md border px-3 py-2 transition-colors ${officeBilling.amount_basis === k ? 'border-amber-400 bg-amber-500/10' : 'border-[#262626] hover:border-[#3f3f3f]'}`}>
                    <p className="text-white text-sm font-medium">{label}</p>
                    <p className="text-[#A3A3A3] text-xs">{hint}</p>
                  </button>
                ))}
              </div>
            </div>

            {(officeBilling.amount_basis === 'final_known' || officeBilling.amount_basis === 'previously_quoted') && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white text-sm">Invoice amount ($)</Label>
                  <Input type="number" inputMode="decimal" data-testid="office-proposed-amount" value={officeBilling.proposed_amount}
                    onChange={(e) => setOfficeBilling((o) => ({ ...o, proposed_amount: e.target.value }))}
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="0.00" />
                </div>
                <div>
                  <Label className="text-white text-sm">Amount already approved ($)</Label>
                  <Input type="number" inputMode="decimal" data-testid="office-approved-amount" value={officeBilling.amount_already_approved}
                    onChange={(e) => setOfficeBilling((o) => ({ ...o, amount_already_approved: e.target.value }))}
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="Optional" />
                </div>
              </div>
            )}
            {officeBilling.amount_basis === 'previously_quoted' && (
              <div>
                <Label className="text-white text-sm">Estimate / Quote ID (optional)</Label>
                <Input data-testid="office-estimate-id" value={officeBilling.estimate_id}
                  onChange={(e) => setOfficeBilling((o) => ({ ...o, estimate_id: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="Link the approved estimate so the office sees the original scope" />
              </div>
            )}

            <div>
              <Label className="text-white text-sm">Invoice description</Label>
              <Textarea data-testid="office-description" value={officeBilling.invoice_description}
                onChange={(e) => setOfficeBilling((o) => ({ ...o, invoice_description: e.target.value }))}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white text-sm">Labor notes / hours</Label>
                <Input data-testid="office-labor" value={officeBilling.labor_notes}
                  onChange={(e) => setOfficeBilling((o) => ({ ...o, labor_notes: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="e.g. 3 hrs, 2 techs" />
              </div>
              <div>
                <Label className="text-white text-sm">PO / reference #</Label>
                <Input data-testid="office-po" value={officeBilling.po_number}
                  onChange={(e) => setOfficeBilling((o) => ({ ...o, po_number: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="Optional" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white text-sm">Materials</Label>
                <Input data-testid="office-materials" value={officeBilling.materials}
                  onChange={(e) => setOfficeBilling((o) => ({ ...o, materials: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="Optional" />
              </div>
              <div>
                <Label className="text-white text-sm">Equipment installed/serviced</Label>
                <Input data-testid="office-equipment" value={officeBilling.equipment_notes}
                  onChange={(e) => setOfficeBilling((o) => ({ ...o, equipment_notes: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="Optional" />
              </div>
            </div>
            <div>
              <Label className="text-white text-sm">Payment terms / why not collected</Label>
              <Input data-testid="office-terms" value={officeBilling.payment_terms}
                onChange={(e) => setOfficeBilling((o) => ({ ...o, payment_terms: e.target.value }))}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="e.g. Net 30, PM billed, PO required" />
            </div>
            <div>
              <Label className="text-white text-sm">Billing notes for the office</Label>
              <Textarea data-testid="office-notes" value={officeBilling.billing_notes}
                onChange={(e) => setOfficeBilling((o) => ({ ...o, billing_notes: e.target.value }))}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1" rows={2} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="office-addl" data-testid="office-additional-work" checked={officeBilling.additional_work}
                onChange={(e) => setOfficeBilling((o) => ({ ...o, additional_work: e.target.checked }))} />
              <Label htmlFor="office-addl" className="text-white text-sm">Additional work occurred beyond the original scope</Label>
            </div>
            {officeBilling.additional_work && (
              <Textarea data-testid="office-additional-notes" value={officeBilling.additional_work_notes}
                onChange={(e) => setOfficeBilling((o) => ({ ...o, additional_work_notes: e.target.value }))}
                className="bg-[#0A0A0A] border-[#262626] text-white" rows={2} placeholder="Describe the additional work for office pricing" />
            )}
            <div>
              <Label className="text-white text-sm">Customer approval name (if applicable)</Label>
              <Input data-testid="office-approval-name" value={officeBilling.customer_approval_name}
                onChange={(e) => setOfficeBilling((o) => ({ ...o, customer_approval_name: e.target.value }))}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="Optional" />
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => goBack()} className="border-[#262626] text-[#A3A3A3]">Back</Button>
              <Button data-testid="office-submit" onClick={submitOfficeBilling} disabled={submitting || !officeBilling.amount_basis}
                className="flex-1 bg-amber-500 text-black hover:bg-amber-400">
                {submitting ? 'Submitting…' : 'Submit to Office for Finalization'}
              </Button>
            </div>
          </div>
        )}

        {step === 'office_submitted' && (
          <div className="space-y-4 mt-8 text-center" data-testid="wiz-office-submitted">
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
              <ClipboardCheck className="w-7 h-7 text-amber-300" />
            </div>
            <h3 className="text-white text-xl font-semibold">Submitted to the office for finalization</h3>
            <p className="text-[#A3A3A3] text-sm max-w-md mx-auto">
              Your job completion and billing details were saved. The office will review, finalize, and send the invoice.
            </p>
            <div className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3 text-left max-w-md mx-auto space-y-1">
              {officeSubmitResult?.work_order_number ? (
                <p className="text-sm text-white">Work order: <span className="font-medium">{officeSubmitResult.work_order_number}</span></p>
              ) : null}
              <p className="text-sm text-white">Amount: <span className="font-medium">
                {officeSubmitResult?.amount_basis === 'final_known' && 'Final amount known'}
                {officeSubmitResult?.amount_basis === 'previously_quoted' && 'Previously quoted amount'}
                {officeSubmitResult?.amount_basis === 'office_to_determine' && 'Office to determine amount'}
                {!officeSubmitResult?.amount_pending && officeSubmitResult?.proposed_amount ? ` — $${Number(officeSubmitResult.proposed_amount).toFixed(2)}` : ''}
              </span></p>
            </div>
            <Button data-testid="office-done" onClick={() => closeDone()} className="bg-amber-500 text-black hover:bg-amber-400">Done</Button>
          </div>
        )}

        {step === 'estimate' && (
          <div className="space-y-4 mt-6" data-testid="wiz-estimate">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Create Estimate</p>
                <p className="text-[#A3A3A3] text-xs mt-1">Customer, job, work order, technician, and service date are already attached from this completion. Add the proposed scope and pricing below.</p>
              </div>
              <Button data-testid="wiz-back-estimate" onClick={goBack} variant="outline" size="sm" className="border-[#262626] text-[#A3A3A3] hover:bg-[#1f1f1f] hover:text-white">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
            </div>

            <div className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3 text-xs text-[#A3A3A3] space-y-0.5">
              <p className="text-white">{appointment.customer_name}</p>
              <p>{appointment.service_type || appointment.shift_title} · WO {appointment.work_order_number || '—'} · {appointment.date}</p>
            </div>

            <div>
              <Label className="text-white text-sm">Estimate title</Label>
              <Input data-testid="est-title" value={estimateForm.title}
                onChange={(e) => setEstimateForm((f) => ({ ...f, title: e.target.value }))}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="Water heater replacement estimate" />
            </div>
            <div>
              <Label className="text-white text-sm">Scope of proposed work</Label>
              <Textarea data-testid="est-scope" value={estimateForm.scope}
                onChange={(e) => setEstimateForm((f) => ({ ...f, scope: e.target.value }))}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1" rows={3} />
            </div>

            <div>
              <Label className="text-white text-sm">Line items</Label>
              <div className="space-y-2 mt-1">
                {estimateForm.line_items.map((li, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`est-line-${i}`}>
                    <Input data-testid={`est-line-desc-${i}`} value={li.description} placeholder="Description"
                      onChange={(e) => updateEstimateLine(i, 'description', e.target.value)}
                      className="col-span-6 bg-[#0A0A0A] border-[#262626] text-white" />
                    <Input data-testid={`est-line-qty-${i}`} type="number" inputMode="decimal" value={li.quantity} placeholder="Qty"
                      onChange={(e) => updateEstimateLine(i, 'quantity', e.target.value)}
                      className="col-span-2 bg-[#0A0A0A] border-[#262626] text-white" />
                    <Input data-testid={`est-line-price-${i}`} type="number" inputMode="decimal" value={li.unit_price} placeholder="Unit $"
                      onChange={(e) => updateEstimateLine(i, 'unit_price', e.target.value)}
                      className="col-span-3 bg-[#0A0A0A] border-[#262626] text-white" />
                    <button type="button" data-testid={`est-line-remove-${i}`} onClick={() => removeEstimateLine(i)} className="col-span-1 text-[#525252] hover:text-red-400 text-lg">×</button>
                  </div>
                ))}
              </div>
              <Button type="button" data-testid="est-add-line" onClick={addEstimateLine} variant="outline" size="sm" className="mt-2 border-[#262626] text-[#A3A3A3]">+ Add service line</Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white text-sm">Discount ($)</Label>
                <Input data-testid="est-discount" type="number" inputMode="decimal" value={estimateForm.discount}
                  onChange={(e) => setEstimateForm((f) => ({ ...f, discount: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>
              <div className="flex items-end justify-end">
                <p className="text-white text-lg font-bold" data-testid="est-total">Total: ${estimateTotal().toFixed(2)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white text-sm">Expiration date</Label>
                <Input data-testid="est-expiration" type="date" value={estimateForm.expiration_date}
                  onChange={(e) => setEstimateForm((f) => ({ ...f, expiration_date: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>
              <div>
                <Label className="text-white text-sm">Follow-up date</Label>
                <Input data-testid="est-followup" type="date" value={estimateForm.follow_up_date}
                  onChange={(e) => setEstimateForm((f) => ({ ...f, follow_up_date: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>
            </div>

            <div>
              <Label className="text-white text-sm">Customer-facing notes</Label>
              <Textarea data-testid="est-customer-notes" value={estimateForm.customer_notes}
                onChange={(e) => setEstimateForm((f) => ({ ...f, customer_notes: e.target.value }))}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1" rows={2} />
            </div>
            <div>
              <Label className="text-white text-sm">Internal office notes</Label>
              <Textarea data-testid="est-internal-notes" value={estimateForm.internal_notes}
                onChange={(e) => setEstimateForm((f) => ({ ...f, internal_notes: e.target.value }))}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1" rows={2} />
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => goBack()} className="border-[#262626] text-[#A3A3A3]">Back</Button>
              <Button data-testid="est-submit" onClick={submitEstimate} disabled={submitting}
                className="flex-1 bg-[#D4AF37] text-black hover:bg-[#F3E5AB]">
                {submitting ? 'Saving…' : 'Save Estimate & Complete Job'}
              </Button>
            </div>
          </div>
        )}

        {step === 'estimate_created' && (
          <div className="space-y-4 mt-8 text-center" data-testid="wiz-estimate-created">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
              <ClipboardCheck className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <h3 className="text-white text-xl font-semibold">Estimate submitted successfully</h3>
            <p className="text-[#A3A3A3] text-sm max-w-md mx-auto">
              Estimate <span className="text-white font-medium">{estimateResult?.estimate_number}</span> was created and linked to this job. It's now in Admin Estimates.
            </p>
            {estimateResult && (
              <div className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3 text-left max-w-md mx-auto">
                <p className="text-sm text-white">Amount: <span className="font-medium">${Number(estimateResult.amount || 0).toFixed(2)}</span></p>
              </div>
            )}
            <Button data-testid="est-done" onClick={() => closeDone()} className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB]">Done</Button>
          </div>
        )}


        {step === 'hsp_details' && (
          <div className="space-y-3 mt-6" data-testid="wiz-hsp-details">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Warranty Claim Details</p>
                <p className="text-[#A3A3A3] text-xs mt-1">
                  The homeowner remains the customer of record. The warranty company is a secondary payor — the office will finalize and send the warranty invoice separately.
                </p>
              </div>
              <Button data-testid="wiz-back-hsp" onClick={goBack} variant="outline" size="sm" className="border-[#262626] text-[#A3A3A3] hover:bg-[#1f1f1f] hover:text-white">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
            </div>
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Warranty Company / Program *</Label>
              <Select
                value={hspForm.warranty_customer_id || (hspForm.provider ? '__other' : '')}
                onValueChange={(v) => {
                  if (v === '__other') {
                    setHspForm({ ...hspForm, warranty_customer_id: null, provider: '' });
                  } else {
                    const co = warrantyCompanies.find((c) => c.id === v);
                    setHspForm({ ...hspForm, warranty_customer_id: v, provider: co ? co.name : '' });
                  }
                }}
              >
                <SelectTrigger data-testid="wiz-hsp-provider-select" className="bg-[#0A0A0A] border-[#262626] text-white mt-1">
                  <SelectValue placeholder={loadingWarrantyCos ? 'Loading warranty companies…' : 'Select a warranty company'} />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {warrantyCompanies.length === 0 && !loadingWarrantyCos && (
                    <div className="px-2 py-1 text-[#A3A3A3] text-xs">
                      No warranty companies on file yet. Use &ldquo;Other (manual entry)&rdquo; below — the office will add it permanently.
                    </div>
                  )}
                  {warrantyCompanies.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="text-white" data-testid={`wiz-warranty-co-${c.id}`}>{c.name}</SelectItem>
                  ))}
                  <SelectItem value="__other" className="text-white" data-testid="wiz-warranty-co-other">Other (manual entry)</SelectItem>
                </SelectContent>
              </Select>
              {!hspForm.warranty_customer_id && (
                <>
                  <Input data-testid="wiz-hsp-provider" value={hspForm.provider}
                    onChange={(e) => setHspForm({ ...hspForm, provider: e.target.value, warranty_customer_id: null })}
                    placeholder="Enter warranty company name"
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-2" />
                  <p className="text-[10px] text-[#D4AF37] mt-1">
                    Heads up: the office will be notified to review and decide whether to convert this into a permanent Warranty Company customer.
                  </p>
                </>
              )}
            </div>
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Warranty Claim / Work Order Number *</Label>
              <Input
                data-testid="wiz-hsp-wo"
                value={hspForm.work_order_number}
                onChange={(e) => {
                  const newValue = e.target.value;
                  // iter122 — Apply the typed value immediately so input feels normal,
                  // BUT if this is the first edit AWAY from the dispatcher's master WO#,
                  // pop the red confirmation modal. Tech can confirm or revert.
                  setHspForm((f) => ({ ...f, work_order_number: newValue }));
                  if (!woEditAck && dispatcherWO && newValue !== dispatcherWO) {
                    setWoConfirmOpen(true);
                  }
                }}
                placeholder="e.g. 261543-001"
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
              />
              {dispatcherWO && (
                <p className="text-[10px] text-[#525252] mt-1">
                  Master schedule Work Order #: <span className="text-[#D4AF37]">{dispatcherWO}</span>
                  {woEditAck && hspForm.work_order_number !== dispatcherWO && (
                    <span className="ml-2 text-orange-400">· edited by tech</span>
                  )}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">HSP / Warranty Approved / Authorized Amount *</Label>
              <Input data-testid="wiz-hsp-approved" type="number" step="0.01" inputMode="decimal" value={hspForm.approved_amount}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setHspForm({ ...hspForm, approved_amount: e.target.value })}
                placeholder="$ — gross amount the warranty approved" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              <p className="text-[10px] text-[#737373] mt-1">Gross amount approved for the covered scope, BEFORE the customer deductible.</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Customer Deductible / Service Fee</Label>
                <Input data-testid="wiz-hsp-deductible" type="number" step="0.01" inputMode="decimal" value={hspForm.deductible}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setHspForm({ ...hspForm, deductible: e.target.value })}
                  placeholder="$0.00" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
                <p className="text-[10px] text-[#737373] mt-1">Customer's contribution within the covered scope.</p>
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Customer Uncovered Balance</Label>
                <Input data-testid="wiz-hsp-uncovered" type="number" step="0.01" inputMode="decimal" value={hspForm.uncovered_balance}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setHspForm({ ...hspForm, uncovered_balance: e.target.value })}
                  placeholder="$0.00" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
                <p className="text-[10px] text-[#737373] mt-1">Customer's uncovered/upgrade balance on covered work.</p>
              </div>
            </div>
            {/* iter302 — Live payer split so the tech sees the math BEFORE continuing.
                Additional customer-only work is added on the next (invoice) step. */}
            {(() => {
              const A = Number(hspForm.approved_amount || 0);
              const D = Number(hspForm.deductible || 0);
              const U = Number(hspForm.uncovered_balance || 0);
              const net = Math.round((A - D) * 100) / 100;
              const cust = Math.round((D + U) * 100) / 100;
              const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
              const bad = D > A;
              return (
                <div data-testid="wiz-hsp-split-preview" className="rounded-md border border-[#D4AF37]/30 bg-gradient-to-b from-[#1a1207] to-[#0A0A0A] p-3 space-y-1.5 text-sm">
                  <Row label="Approved / Authorized" value={fmt(A)} subtle />
                  {D > 0 && <Row label="Less: Customer Deductible" value={`-${fmt(D)}`} negative />}
                  <Row label="Warranty Company owes (net)" value={fmt(Math.max(net, 0))} highlight />
                  <div className="border-t border-[#D4AF37]/30 my-1.5" />
                  <Row label="Customer core (deductible + uncovered)" value={fmt(cust)} />
                  <p className="text-[10px] text-[#737373]">Additional customer-only work is added on the next step.</p>
                  {bad && <p data-testid="wiz-hsp-split-error" className="text-[11px] text-red-400 font-medium">Deductible cannot exceed the approved amount.</p>}
                </div>
              );
            })()}
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Notes for Office Review</Label>
              <Textarea data-testid="wiz-hsp-notes" value={hspForm.notes}
                onChange={(e) => setHspForm({ ...hspForm, notes: e.target.value })}
                rows={2}
                placeholder="Authorization details, anything the office needs to know"
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
            </div>
            <Button data-testid="wiz-hsp-continue" onClick={submitHspDetails} disabled={submitting}
              className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
              Continue → Customer Invoice (${(Number(hspForm.deductible || 0) + Number(hspForm.uncovered_balance || 0)).toFixed(2)} customer core)
            </Button>
          </div>
        )}

        {step === 'invoice' && (
          <div className="space-y-4 mt-4 sm:mt-6 pb-4" data-testid="wiz-invoice">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <p className="text-[#A3A3A3] text-xs sm:text-sm flex-1 min-w-[200px]">Customer, work, and materials are auto-attached. Pick the right kind per line — <span className="text-[#D4AF37]">Membership</span> sales are never discounted.</p>
              <Button data-testid="wiz-back-invoice" onClick={goBack} variant="outline" size="sm" className="border-[#262626] text-[#A3A3A3] hover:bg-[#1f1f1f] hover:text-white shrink-0">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
            </div>

            {/* iter112 — Line items with kind selector. On mobile each line gets its own
                stacked card; on desktop the grid still keeps things compact. */}
            <div className="space-y-3">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Line Items</Label>
              {invoiceForm.line_items.map((li, idx) => {
                const isMembership = (li.kind || 'service') === 'membership';
                // iter112 — Feed each controlled <Input> a defined string so React
                // doesn't emit the "uncontrolled→controlled" warning when a brand-new
                // line is typed into for the first time.
                const qtyVal   = (li.quantity === 0 || li.quantity == null) ? '' : String(li.quantity);
                const priceVal = (li.unit_price === 0 || li.unit_price == null) ? '' : String(li.unit_price);
                const updateLine = (patch) => setInvoiceForm((prev) => ({
                  ...prev,
                  line_items: prev.line_items.map((x, i) => i === idx ? { ...x, ...patch } : x),
                }));
                return (
                  <div key={idx} data-testid={`wiz-inv-line-${idx}`} className={`rounded-md border p-2.5 space-y-2 ${isMembership ? 'border-[#D4AF37]/40 bg-[#1a1207]' : 'border-[#262626] bg-[#0A0A0A]'}`}>
                    {/* Row 1: kind + delete */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          data-testid={`wiz-inv-line-${idx}-kind-service`}
                          onClick={() => updateLine({ kind: 'service' })}
                          className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors ${
                            !isMembership ? 'bg-[#D4AF37] text-[#0A0A0A]' : 'bg-[#1a1a1a] text-[#A3A3A3] hover:text-white'
                          }`}
                        >Service</button>
                        <button
                          type="button"
                          data-testid={`wiz-inv-line-${idx}-kind-membership`}
                          onClick={() => {
                            updateLine({ kind: 'membership' });
                            // iter112 — Adding a membership line auto-prefills 10% on
                            // the service-line discount (matches "the customer is now
                            // a member, give them the 10% on the same invoice" flow).
                            if (Number(invoiceForm.membership_discount_percent || 0) === 0) {
                              setInvoiceForm((p) => ({ ...p, membership_discount_percent: 10 }));
                            }
                          }}
                          className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors ${
                            isMembership ? 'bg-[#D4AF37] text-[#0A0A0A]' : 'bg-[#1a1a1a] text-[#A3A3A3] hover:text-white'
                          }`}
                        >Membership</button>
                      </div>
                      <Button size="sm" variant="ghost"
                        data-testid={`wiz-inv-line-${idx}-remove`}
                        onClick={() => setInvoiceForm({ ...invoiceForm, line_items: invoiceForm.line_items.filter((_, i) => i !== idx) })}
                        className="text-red-400 h-7 w-7 p-0 shrink-0"
                      ><X className="w-3.5 h-3.5" /></Button>
                    </div>
                    {/* Row 2: description */}
                    <Input data-testid={`wiz-inv-line-${idx}-desc`} value={li.description || ''}
                      onChange={(e) => updateLine({ description: e.target.value })}
                      placeholder={isMembership ? 'Membership tier (e.g. Protect)' : 'Description (e.g. 50 gal water heater)'}
                      className="bg-[#141414] border-[#262626] text-white" />
                    {/* Row 3: qty + price */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-[#737373] uppercase tracking-wider">Qty</Label>
                        <Input data-testid={`wiz-inv-line-${idx}-qty`} type="number" inputMode="decimal" step="0.5"
                          value={qtyVal}
                          placeholder="1"
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => updateLine({ quantity: e.target.value === '' ? 0 : Number(e.target.value) })}
                          className="bg-[#141414] border-[#262626] text-white" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#737373] uppercase tracking-wider">Unit Price</Label>
                        <Input data-testid={`wiz-inv-line-${idx}-price`} type="number" inputMode="decimal" step="0.01"
                          value={priceVal}
                          placeholder="0.00"
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => updateLine({ unit_price: e.target.value === '' ? 0 : Number(e.target.value) })}
                          className="bg-[#141414] border-[#262626] text-white" />
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="wiz-inv-add-line" size="sm" variant="outline"
                  onClick={() => setInvoiceForm({ ...invoiceForm, line_items: [...invoiceForm.line_items, { description: '', quantity: 1, unit_price: 0, kind: 'service' }] })}
                  className="border-[#262626] text-white min-h-[44px]">
                  <Plus className="w-3 h-3 mr-1" /> Add Service Line
                </Button>
                <Button data-testid="wiz-inv-add-membership" size="sm" variant="outline"
                  onClick={() => setShowMembershipPicker(true)}
                  className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 min-h-[44px]">
                  <Plus className="w-3 h-3 mr-1" /> Add Membership Sale
                </Button>
                <MembershipPlanPicker
                  open={showMembershipPicker}
                  onOpenChange={setShowMembershipPicker}
                  currentMembership={customerMembership}
                  onSelect={(line) => setInvoiceForm((prev) => ({
                    ...prev,
                    line_items: [...prev.line_items, line],
                    // Selling/adding a membership auto-prefills the 10% service discount when none set.
                    membership_discount_percent: Number(prev.membership_discount_percent || 0) === 0 ? 10 : prev.membership_discount_percent,
                  }))}
                />
              </div>
            </div>

            {/* iter112 — Discount controls. Member discount % is the main lever. Other
                discount stays available but secondary. Override total is admin-style and
                hidden behind a disclosure so it doesn't dominate the screen. */}
            <div className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">
                    Member Discount %{customerHasActiveMembership && <span className="text-[#D4AF37] ml-1">· Active member</span>}
                  </Label>
                  <Input data-testid="wiz-inv-member-pct" type="number" inputMode="decimal" step="1" min="0" max="100"
                    value={(invoiceForm.membership_discount_percent === 0 || invoiceForm.membership_discount_percent == null) ? '' : String(invoiceForm.membership_discount_percent)}
                    placeholder={customerHasActiveMembership ? '10' : '0'}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, membership_discount_percent: e.target.value === '' ? 0 : Number(e.target.value) })}
                    className="bg-[#141414] border-[#262626] text-white mt-1" />
                  <p className="text-[10px] text-[#737373] mt-1">Applies only to service lines.</p>
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Other Discount ($)</Label>
                  <Input data-testid="wiz-inv-discount" type="number" inputMode="decimal" step="0.01" min="0"
                    value={(invoiceForm.discount === 0 || invoiceForm.discount == null) ? '' : String(invoiceForm.discount)}
                    placeholder="0.00"
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, discount: e.target.value === '' ? 0 : Number(e.target.value) })}
                    className="bg-[#141414] border-[#262626] text-white mt-1" />
                  <p className="text-[10px] text-[#737373] mt-1">Manager-approved promo, etc.</p>
                </div>
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-[#737373] hover:text-[#A3A3A3] select-none">Admin: Override Total</summary>
                <div className="mt-2">
                  <Input data-testid="wiz-inv-amount" type="number" inputMode="decimal" step="0.01" min="0"
                    value={invoiceForm.amount}
                    placeholder="Leave blank for auto-calculation"
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                    className="bg-[#141414] border-[#262626] text-white" />
                  <p className="text-[10px] text-[#737373] mt-1">When set, replaces the computed total above. Use sparingly.</p>
                </div>
              </details>
            </div>

            {/* iter112 — Clear math summary panel. The tech sees exactly what the customer owes
                and how it was calculated, before saving. */}
            {(() => {
              const items = invoiceForm.line_items || [];
              const subSvc = items.filter((li) => (li.kind || 'service') === 'service').reduce((s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0), 0);
              const subMem = items.filter((li) => li.kind === 'membership').reduce((s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0), 0);
              const memPct = Math.max(0, Math.min(100, Number(invoiceForm.membership_discount_percent || 0)));
              const memDisc = Math.round((subSvc * memPct / 100) * 100) / 100;
              const otherDisc = Math.max(0, Number(invoiceForm.discount || 0));
              const svcTotal = Math.max(0, subSvc - memDisc - otherDisc);
              const computed = Math.round((svcTotal + subMem) * 100) / 100;
              const overrideVal = invoiceForm.amount === '' || invoiceForm.amount === null ? null : Number(invoiceForm.amount);
              const finalTotal = overrideVal && overrideVal > 0 ? overrideVal : computed;
              const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
              return (
                <div data-testid="wiz-inv-math" className="rounded-md border border-[#D4AF37]/30 bg-gradient-to-b from-[#1a1207] to-[#0A0A0A] p-3 space-y-1.5 text-sm">
                  <Row label="Service subtotal" value={fmt(subSvc)} />
                  {memPct > 0 && (
                    <Row label={`Membership discount (${memPct}%)`} value={`-${fmt(memDisc)}`} negative />
                  )}
                  {otherDisc > 0 && (
                    <Row label="Other discount" value={`-${fmt(otherDisc)}`} negative />
                  )}
                  <Row label="Service total" value={fmt(svcTotal)} subtle />
                  {subMem > 0 && (
                    <Row label="Membership sale" value={`+${fmt(subMem)}`} />
                  )}
                  <div className="border-t border-[#D4AF37]/30 my-1.5" />
                  <Row label="Invoice total" value={fmt(finalTotal)} highlight />
                  {overrideVal && overrideVal > 0 && (
                    <p data-testid="wiz-inv-override-note" className="text-[10px] text-[#D4AF37] italic">Admin override applied (computed was {fmt(computed)})</p>
                  )}
                </div>
              );
            })()}

            {/* iter302 — Payer reconciliation. On an HSP/warranty job, show the full
                split so the tech can visually verify Customer + Warranty = Total Job. */}
            {hspAllocation && (() => {
              const A = Number(hspAllocation.approved_amount || 0);
              const D = Number(hspAllocation.deductible || 0);
              const U = Number(hspAllocation.uncovered_balance || 0);
              const X = Number(hspAllocation.net_warranty_amount || 0);  // warranty owes
              const items = invoiceForm.line_items || [];
              const custItems = items.reduce((s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0), 0);
              const overrideVal = invoiceForm.amount === '' || invoiceForm.amount === null ? null : Number(invoiceForm.amount);
              const Y = overrideVal && overrideVal > 0 ? overrideVal : custItems;  // customer owes
              const E = Math.round((Y - D - U) * 100) / 100;  // additional customer-only work
              const Z = Math.round((X + Y) * 100) / 100;      // total job value
              const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
              return (
                <div data-testid="wiz-inv-payer-split" className="rounded-md border border-[#3b82f6]/40 bg-gradient-to-b from-[#0b1220] to-[#0A0A0A] p-3 space-y-1.5 text-sm">
                  <p className="text-[11px] text-[#93c5fd] uppercase tracking-wider font-bold mb-1">Payer Split — HSP/Warranty</p>
                  <Row label="Approved / Authorized" value={fmt(A)} subtle />
                  <Row label="Customer Deductible" value={fmt(D)} subtle />
                  <Row label="Customer Uncovered Balance" value={fmt(U)} subtle />
                  <Row label="Additional Customer Work" value={fmt(E > 0 ? E : 0)} subtle />
                  <div className="border-t border-[#3b82f6]/30 my-1.5" />
                  <Row label="Customer Owes" value={fmt(Y)} highlight />
                  <Row label="Warranty Company Owes" value={fmt(X)} highlight />
                  <div className="border-t border-[#3b82f6]/30 my-1.5" />
                  <Row label="Total Job Value" value={fmt(Z)} highlight />
                  {Math.round(custItems * 100) === 0 && X > 0 && (
                    <p data-testid="wiz-inv-warranty-only-note" className="text-[11px] text-[#93c5fd]">Customer owes $0 — this will create the warranty invoice only.</p>
                  )}
                </div>
              );
            })()}

            <Button data-testid="wiz-inv-submit" onClick={createInvoice} disabled={submitting}
              className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] min-h-[48px] font-bold uppercase tracking-wider">
              <DollarSign className="w-4 h-4 mr-2" />
              {submitting ? 'Saving...' : (hspAllocation && ((invoiceForm.line_items || []).reduce((s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0), 0) === 0)) ? 'Record Warranty Claim (No Customer Charge)' : invoiceForm.save_as_draft ? 'Save Draft Invoice' : 'Save Customer Invoice & Continue'}
            </Button>
          </div>
        )}

        {step === 'payment' && (
          <div className="space-y-4 mt-4 sm:mt-6 pb-4" data-testid="wiz-payment">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <p className="text-[#A3A3A3] text-xs sm:text-sm flex-1 min-w-[200px]">Record how today's customer balance was resolved. This <span className="text-white">unlocks your next job.</span></p>
              <Button data-testid="wiz-back-payment" onClick={goBack} variant="outline" size="sm" className="border-[#262626] text-[#A3A3A3] hover:bg-[#1f1f1f] hover:text-white shrink-0">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
            </div>
            {/* iter112 — Payment disposition picker. 2 cols on mobile, stays 2 on desktop
                for fat-finger comfort. Each tile is ≥ 64px tall (Apple touch-target guideline). */}
            <div className="grid grid-cols-2 gap-2">
              {PAYMENT_DISPOSITIONS.map((d) => (
                <button
                  key={d.key}
                  data-testid={`wiz-pay-${d.key}`}
                  type="button"
                  onClick={() => setPaymentForm({ ...paymentForm, disposition: d.key, amount: PAID_ONSITE_KEYS.includes(d.key) ? paymentForm.amount : '' })}
                  className={`p-3 rounded-md border text-left transition-colors min-h-[64px] ${
                    paymentForm.disposition === d.key
                      ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]'
                      : 'bg-[#0A0A0A] border-[#262626] text-white hover:border-[#3f3f3f]'
                  }`}
                >
                  <d.icon className="w-4 h-4 mb-1" />
                  <p className="text-xs font-medium">{d.label}</p>
                </button>
              ))}
            </div>

            {/* iter113 — Customer Paying Card Now: Stripe-mediated. The tech opens
                Checkout in a new tab; the wizard polls until the webhook confirms
                payment, then auto-advances. No manual card-paid recording allowed. */}
            {paymentForm.disposition === 'paying_card_now' && (
              <div data-testid="wiz-pay-card-now-panel" className="rounded-md border border-[#D4AF37]/40 bg-gradient-to-b from-[#1a1207] to-[#0A0A0A] p-3 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Stripe Card Collection</p>
                  {cardNow.paid && <span className="text-green-400 text-xs font-bold uppercase tracking-wider">✓ Paid</span>}
                  {!cardNow.paid && cardNow.sessionId && <span className="text-yellow-400 text-xs animate-pulse">Waiting on Stripe…</span>}
                </div>
                {!cardNow.sessionId ? (
                  <>
                    <p className="text-sm text-white">Opens a secure Stripe Checkout page in a new tab so the customer can pay by card on this device. The invoice is marked paid automatically once Stripe confirms.</p>
                    <Button
                      data-testid="wiz-pay-card-now-open"
                      onClick={startStripeCardNow}
                      disabled={cardNow.opening}
                      className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] min-h-[48px] font-bold uppercase tracking-wider"
                    >
                      {cardNow.opening ? 'Opening…' : 'Open Stripe Checkout'}
                    </Button>
                  </>
                ) : cardNow.paid ? (
                  <p className="text-sm text-green-400">Payment confirmed — you can close the job below.</p>
                ) : (
                  <>
                    <p className="text-sm text-white">Stripe Checkout opened in a new tab. Hand the phone to the customer. The wizard will auto-detect payment within a few seconds of confirmation.</p>
                    <Button
                      data-testid="wiz-pay-card-now-reopen"
                      onClick={() => cardNow.url && window.open(cardNow.url, '_blank', 'noopener')}
                      variant="outline"
                      className="w-full border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 min-h-[44px]"
                    >
                      Re-open Stripe Tab
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* iter113 — Deferred / Unable to Collect Today requires a reason. */}
            {paymentForm.disposition === 'deferred_unable_collect' && (
              <div data-testid="wiz-pay-deferred-required" className="rounded-md border border-[#D4AF37]/40 bg-[#1a1207] p-3 space-y-3">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Required reason</p>
                <div className="grid grid-cols-1 gap-1.5">
                  {[
                    { v: 'customer_not_available', l: 'Customer Not Available' },
                    { v: 'manager_approved',      l: 'Manager-Approved Deferred Payment' },
                    { v: 'waiting_approval',      l: 'Waiting on Approval' },
                    { v: 'link_will_send',        l: 'Payment Link Will Be Sent' },
                    { v: 'other',                 l: 'Other (add a note below)' },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`wiz-pay-deferred-${opt.v}`}
                      onClick={() => setPaymentForm({ ...paymentForm, payment_method_detail: opt.v })}
                      className={`text-left rounded-md border px-3 py-2 text-sm min-h-[44px] transition-colors ${
                        paymentForm.payment_method_detail === opt.v
                          ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A] font-medium'
                          : 'bg-[#0A0A0A] border-[#262626] text-white hover:border-[#3f3f3f]'
                      }`}
                    >{opt.l}</button>
                  ))}
                </div>
              </div>
            )}

            {/* iter112 — Customer Paid — Other requires method detail + reference. */}
            {paymentForm.disposition === 'paid_other' && (
              <div data-testid="wiz-pay-other-required" className="rounded-md border border-[#D4AF37]/40 bg-[#1a1207] p-3 space-y-3">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Required for "Paid — Other"</p>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Payment Method Detail</Label>
                  <select
                    data-testid="wiz-pay-other-method"
                    value={paymentForm.payment_method_detail}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_method_detail: e.target.value })}
                    className="w-full bg-[#141414] border border-[#262626] text-white mt-1 rounded-md px-3 py-2 min-h-[44px]"
                  >
                    <option value="">— Pick one —</option>
                    {/* iter196 — Strict allow-list. Office needs one of these three
                        exactly so reconciliation is unambiguous. Legacy free-text
                        options (Venmo/Cash App/PayPal/External card terminal/Manager
                        approved/Other) were removed per Dan's request. */}
                    {[
                      { v: 'quickbooks_pay', l: 'QuickBooks Pay / GoPayment' },
                      { v: 'zelle',          l: 'Zelle' },
                      { v: 'already_paid',   l: 'Already paid' },
                    ].map((opt) => (
                      <option key={opt.v} value={opt.v}>{opt.l}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">
                  Amount Collected On Site{PAID_ONSITE_KEYS.includes(paymentForm.disposition) ? ' *' : ''}
                </Label>
                <Input data-testid="wiz-pay-amount" type="number" inputMode="decimal" step="0.01" min="0"
                  value={PAID_ONSITE_KEYS.includes(paymentForm.disposition) ? paymentForm.amount : ''}
                  disabled={!PAID_ONSITE_KEYS.includes(paymentForm.disposition)}
                  placeholder={PAID_ONSITE_KEYS.includes(paymentForm.disposition) ? '0.00' : '$0.00 — no on-site collection'}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1 disabled:opacity-40" />
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">
                  {paymentForm.disposition === 'paid_check'
                    ? 'Check #'
                    : paymentForm.disposition === 'paid_quickbooks'
                      ? 'QB Confirmation #'
                      : paymentForm.disposition === 'paid_card'
                        ? 'Card Last 4'
                        : (paymentForm.disposition === 'paid_other' && paymentForm.payment_method_detail === 'quickbooks_pay')
                          ? 'Auth Code / Last 4'
                          : 'Reference'}
                  {paymentForm.disposition === 'paid_other' ? ' *' : ''}
                </Label>
                <Input data-testid="wiz-pay-ref" value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  placeholder={
                    paymentForm.disposition === 'paid_check'
                      ? 'Check number'
                      : paymentForm.payment_method_detail === 'quickbooks_pay'
                        ? 'QB auth code / last 4'
                        : paymentForm.payment_method_detail === 'zelle'
                          ? 'Zelle confirmation #'
                          : paymentForm.payment_method_detail === 'already_paid'
                            ? 'How / when paid'
                            : paymentForm.disposition === 'paid_other'
                              ? 'Confirmation # / last 4 / note'
                              : 'Check#, card last4, etc.'
                  }
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>
            </div>
            <Textarea data-testid="wiz-pay-notes" value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              rows={2} placeholder="Notes (optional)" className="bg-[#0A0A0A] border-[#262626] text-white" />

            {/* iter112 — Pre-close confirmation. Shows the tech the full picture before
                they hit "Close Job & Unlock Next" so payment status is always clear. */}
            {(() => {
              const dispoMeta = PAYMENT_DISPOSITIONS.find((d) => d.key === paymentForm.disposition);
              const invTotal = Number(paymentForm.amount || 0);
              const collected = paymentForm.disposition?.startsWith('paid_') ? Number(paymentForm.amount || 0) : 0;
              const balanceDue = Math.max(0, invTotal - collected);
              const linkSent = paymentForm.disposition === 'payment_link_sent';
              const deferred = paymentForm.disposition === 'deferred_payment';
              const noBalance = paymentForm.disposition === 'no_balance_due';
              return (
                <div data-testid="wiz-pay-summary" className="rounded-md border border-[#D4AF37]/30 bg-gradient-to-b from-[#1a1207] to-[#0A0A0A] p-3 space-y-1.5 text-sm">
                  <p className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.22em]">Closeout Summary</p>
                  <Row label="Method" value={dispoMeta?.label || '—'} />
                  {paymentForm.disposition === 'paid_other' && paymentForm.payment_method_detail && (
                    <Row label="Method detail" value={paymentForm.payment_method_detail} subtle />
                  )}
                  <Row label="Invoice total" value={`$${invTotal.toFixed(2)}`} />
                  {paymentForm.disposition?.startsWith('paid_') && (
                    <Row label="Amount collected" value={`$${collected.toFixed(2)}`} highlight />
                  )}
                  {!noBalance && balanceDue > 0 && (
                    <Row label="Balance due" value={`$${balanceDue.toFixed(2)}`} negative />
                  )}
                  {linkSent    && <Row label="Status" value="Payment link sent — awaiting customer" subtle />}
                  {deferred    && <Row label="Status" value="Deferred — manager approved" subtle />}
                  {noBalance   && <Row label="Status" value="No customer balance due" subtle />}
                </div>
              );
            })()}

            <Button data-testid="wiz-pay-submit" onClick={recordPayment} disabled={submitting}
              className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] min-h-[48px] font-bold uppercase tracking-wider">
              {submitting ? 'Recording...' : 'Close Job & Unlock Next'}
            </Button>
          </div>
        )}

        {/* iter114/iter119 — Post-close Membership Onboarding gate. Only fires when
            the invoice had a kind=membership line. Replaces the iter115 inline
            "Asset Log" pressure with three onboarding paths (the tech is no
            longer forced to build a full Plumbing Profile at closeout). */}
        {showMembershipPrompt && (
          <div data-testid="wiz-membership-onboarding-overlay" className="absolute inset-0 z-30 bg-[#0A0A0A]/95 backdrop-blur-sm overflow-y-auto p-4 sm:p-6 flex items-start">
            <div className="w-full max-w-lg mx-auto space-y-4 mt-4 sm:mt-8">
              <div>
                <p className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.22em]">Membership Sold Today</p>
                <h3 className="text-white text-xl font-bold mt-1">How should onboarding be handled?</h3>
                <p className="text-[#A3A3A3] text-sm mt-1">Recommended: schedule a separate onboarding visit so the Plumbing Profile gets built properly and the membership feels valuable.</p>
              </div>

              {!membershipAssetMode && (
                <div className="grid grid-cols-1 gap-2">
                  {/* PRIMARY — Schedule Onboarding Inspection */}
                  <Button
                    data-testid="wiz-onboarding-schedule"
                    onClick={() => setMembershipAssetMode('schedule')}
                    className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] min-h-[60px] font-bold uppercase tracking-wider justify-start px-4 text-left"
                  >
                    <span className="block">
                      <span className="block">Schedule Membership Onboarding Inspection</span>
                      <span className="block text-[10px] font-normal opacity-80 normal-case mt-0.5">Recommended — Plumbing Profile built on a separate visit</span>
                    </span>
                  </Button>

                  {/* SECONDARY — Quick Setup Now */}
                  <Button
                    data-testid="wiz-onboarding-quick"
                    onClick={() => setMembershipAssetMode('quick')}
                    variant="outline"
                    className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 min-h-[60px] justify-start px-4 text-left"
                  >
                    <span className="block">
                      <span className="block font-bold uppercase tracking-wider">Complete Quick Setup Now</span>
                      <span className="block text-[10px] font-normal opacity-80 normal-case mt-0.5">Only if you have time on-site today</span>
                    </span>
                  </Button>

                  {/* TERTIARY — Office Follow-Up */}
                  <Button
                    data-testid="wiz-onboarding-office"
                    onClick={() => setMembershipAssetMode('office_followup')}
                    variant="outline"
                    className="border-[#262626] text-white hover:bg-[#1f1f1f] min-h-[60px] justify-start px-4 text-left"
                  >
                    <span className="block">
                      <span className="block font-bold uppercase tracking-wider">Office Follow-Up Needed</span>
                      <span className="block text-[10px] font-normal opacity-80 normal-case mt-0.5">Customer or dispatch handles scheduling later</span>
                    </span>
                  </Button>

                  <button
                    data-testid="wiz-onboarding-skip"
                    onClick={() => { setShowMembershipPrompt(false); closeDone(); }}
                    className="text-[#525252] text-xs hover:text-[#A3A3A3] underline self-start mt-2 px-1"
                  >
                    Not a real membership / skip
                  </button>
                </div>
              )}

              {/* SCHEDULE — collects optional preferred date + WH flush toggle */}
              {membershipAssetMode === 'schedule' && (
                <div data-testid="wiz-onboarding-schedule-form" className="space-y-3 rounded-md border border-[#D4AF37]/30 bg-[#141414] p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Schedule Onboarding Inspection</p>
                    <button onClick={() => setMembershipAssetMode(null)} className="text-[#A3A3A3] hover:text-white text-xs">← Back</button>
                  </div>
                  <p className="text-sm text-white">
                    A draft will be added to the <span className="text-[#D4AF37]">Unscheduled rail</span> titled{' '}
                    <span className="text-[#D4AF37] font-bold">
                      {includeWHFlush ? 'Membership Onboarding Inspection + Water Heater Flush' : 'Membership Onboarding Inspection'}
                    </span>{' '}
                    so dispatch can schedule the deeper visit.
                  </p>
                  <label className="flex items-center gap-2 text-sm text-white cursor-pointer select-none">
                    <input
                      data-testid="wiz-onboarding-wh-flush"
                      type="checkbox"
                      checked={includeWHFlush}
                      onChange={(e) => setIncludeWHFlush(e.target.checked)}
                      className="accent-[#D4AF37] w-4 h-4"
                    />
                    Include water heater flush (if membership covers it)
                  </label>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Preferred Date (optional)</Label>
                    <Input
                      data-testid="wiz-onboarding-preferred-date"
                      type="date"
                      value={onboardingPreferredDate}
                      onChange={(e) => setOnboardingPreferredDate(e.target.value)}
                      className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Notes for Dispatch (optional)</Label>
                    <Textarea
                      data-testid="wiz-onboarding-schedule-notes"
                      rows={2}
                      value={onboardingNotes}
                      onChange={(e) => setOnboardingNotes(e.target.value)}
                      placeholder="Anything dispatch should know before scheduling…"
                      className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                    />
                  </div>
                  <Button
                    data-testid="wiz-onboarding-schedule-submit"
                    onClick={() => submitOnboardingDecision('schedule')}
                    disabled={assetSubmitting}
                    className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] min-h-[48px] font-bold uppercase tracking-wider"
                  >
                    {assetSubmitting ? 'Scheduling…' : 'Create Onboarding Draft & Finish'}
                  </Button>
                </div>
              )}

              {/* QUICK — confirmation only; tech logs records via Plumbing Profile after closeout */}
              {membershipAssetMode === 'quick' && (
                <div data-testid="wiz-onboarding-quick-form" className="space-y-3 rounded-md border border-[#D4AF37]/30 bg-[#141414] p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Complete Quick Setup Now</p>
                    <button onClick={() => setMembershipAssetMode(null)} className="text-[#A3A3A3] hover:text-white text-xs">← Back</button>
                  </div>
                  <p className="text-sm text-white">
                    Mark the membership as <span className="text-[#D4AF37]">Quick Setup Completed</span>. Open the customer&rsquo;s{' '}
                    <span className="text-[#D4AF37]">Plumbing Profile</span> from the admin app to log equipment and photos when you have a moment.
                  </p>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Quick Notes (optional)</Label>
                    <Textarea
                      data-testid="wiz-onboarding-quick-notes"
                      rows={2}
                      value={onboardingNotes}
                      onChange={(e) => setOnboardingNotes(e.target.value)}
                      placeholder="What did you document on-site today?"
                      className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                    />
                  </div>
                  <Button
                    data-testid="wiz-onboarding-quick-submit"
                    onClick={() => submitOnboardingDecision('quick')}
                    disabled={assetSubmitting}
                    className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] min-h-[48px] font-bold uppercase tracking-wider"
                  >
                    {assetSubmitting ? 'Saving…' : 'Mark Quick Setup & Finish'}
                  </Button>
                </div>
              )}

              {/* OFFICE — drops a high-priority dispatch task */}
              {membershipAssetMode === 'office_followup' && (
                <div data-testid="wiz-onboarding-office-form" className="space-y-3 rounded-md border border-[#D4AF37]/30 bg-[#141414] p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Office Follow-Up Needed</p>
                    <button onClick={() => setMembershipAssetMode(null)} className="text-[#A3A3A3] hover:text-white text-xs">← Back</button>
                  </div>
                  <p className="text-sm text-white">
                    Drop a <span className="text-[#D4AF37]">high-priority</span> task into the Unscheduled rail —{' '}
                    <span className="text-[#D4AF37] font-bold">Membership Onboarding Inspection Needed</span>. Office will call the customer to schedule.
                  </p>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Notes for Office (optional)</Label>
                    <Textarea
                      data-testid="wiz-onboarding-office-notes"
                      rows={2}
                      value={onboardingNotes}
                      onChange={(e) => setOnboardingNotes(e.target.value)}
                      placeholder="Anything the office should know before calling…"
                      className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                    />
                  </div>
                  <Button
                    data-testid="wiz-onboarding-office-submit"
                    onClick={() => submitOnboardingDecision('office_followup')}
                    disabled={assetSubmitting}
                    className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] min-h-[48px] font-bold uppercase tracking-wider"
                  >
                    {assetSubmitting ? 'Creating…' : 'Create Office Follow-Up & Finish'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

      </SheetContent>

      {/* iter122 — Red confirmation modal when tech changes the dispatcher's master
          Work Order #. "No, Keep Dispatcher Work Order #" reverts the field. */}
      {woConfirmOpen && (
        <div
          data-testid="wiz-wo-confirm-overlay"
          className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="wo-confirm-title"
        >
          <div className="w-full max-w-md bg-[#1A0A0A] border-2 border-red-600 rounded-md shadow-[0_0_40px_rgba(220,38,38,0.4)] p-5">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-red-600/20 border-2 border-red-500 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-300" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 id="wo-confirm-title" className="text-red-300 font-bold uppercase tracking-wider text-sm">
                  Master Work Order # Override
                </h3>
                <p className="text-white mt-2 text-sm leading-relaxed">
                  You are changing this from the master schedule Work Order&nbsp;#. Do you wish to continue?
                </p>
                <div className="mt-3 p-2 bg-[#0A0A0A] border border-red-700/40 rounded">
                  <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Master Schedule</p>
                  <p className="text-[#D4AF37] font-mono text-sm">{dispatcherWO || '—'}</p>
                  <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider mt-2">Your Edit</p>
                  <p className="text-orange-300 font-mono text-sm">{hspForm.work_order_number || '(blank)'}</p>
                </div>
              </div>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-2">
              <Button
                data-testid="wiz-wo-confirm-revert"
                onClick={() => {
                  setHspForm((f) => ({ ...f, work_order_number: dispatcherWO }));
                  setWoConfirmOpen(false);
                }}
                variant="outline"
                className="flex-1 border-[#262626] text-white hover:bg-[#262626] min-h-[44px]"
              >
                No, Keep Dispatcher Work Order&nbsp;#
              </Button>
              <Button
                data-testid="wiz-wo-confirm-continue"
                onClick={() => {
                  setWoEditAck(true);
                  setWoConfirmOpen(false);
                }}
                className="flex-1 bg-red-600 text-white hover:bg-red-500 font-bold min-h-[44px]"
              >
                Yes, Continue Editing
              </Button>
            </div>
          </div>
        </div>
      )}
    </Sheet>
  );
};

const StepDots = ({ step }) => {
  const stages = ['form', 'invoice', 'payment'];
  const idx = stages.indexOf(step);
  return (
    <div className="flex items-center gap-2 mt-3" data-testid="wiz-stepdots">
      {stages.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`h-1.5 rounded-full transition-colors ${i <= idx ? 'bg-[#D4AF37]' : 'bg-[#262626]'} flex-1`} />
          {i < stages.length - 1 && <span className="text-[#262626] text-[8px]">→</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

// iter112 — Single-line row used by both invoice math and payment closeout summaries.
const Row = ({ label, value, highlight, subtle, negative }) => (
  <div className="flex items-center justify-between gap-2">
    <span className={`${subtle ? 'text-[#737373] text-xs' : highlight ? 'text-[#D4AF37] font-bold text-base uppercase tracking-wider' : 'text-[#A3A3A3] text-sm'}`}>{label}</span>
    <span className={`${negative ? 'text-red-400' : highlight ? 'text-[#D4AF37] font-bold text-base' : 'text-white font-medium'}`}>{value}</span>
  </div>
);

const PHOTO_KINDS = [
  { key: 'equipment',  label: 'Equipment Photo' },
  { key: 'data_plate', label: 'Data Plate / Serial Tag' },
  { key: 'installed',  label: 'Installed Equipment' },
];

const AssetPhotoCapture = ({ photos, onChange }) => {
  // photos: [{kind, data_url}]
  const handleFile = (kind) => async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large — max 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const next = [...(photos || []).filter((p) => p.kind !== kind), { kind, data_url: dataUrl }];
      onChange(next);
    };
    reader.readAsDataURL(file);
  };
  const removePhoto = (kind) => {
    onChange((photos || []).filter((p) => p.kind !== kind));
  };
  return (
    <div className="grid grid-cols-3 gap-2">
      {PHOTO_KINDS.map((p) => {
        const existing = (photos || []).find((x) => x.kind === p.key);
        return (
          <label key={p.key} className="bg-[#0A0A0A] border border-dashed border-[#3f3f3f] rounded-md p-2 text-center cursor-pointer hover:border-[#D4AF37] transition-colors" data-testid={`wiz-eq-photo-${p.key}`}>
            <input type="file" accept="image/*" onChange={handleFile(p.key)} className="hidden" />
            {existing ? (
              <div className="relative">
                <img src={existing.data_url} alt={p.label} className="w-full h-20 object-cover rounded" />
                <button type="button" onClick={(ev) => { ev.preventDefault(); removePhoto(p.key); }} className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">×</button>
                <p className="text-[10px] text-[#A3A3A3] mt-1">{p.label}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-20">
                <Camera className="w-4 h-4 text-[#525252] mb-1" />
                <p className="text-[10px] text-[#A3A3A3] leading-tight">{p.label}</p>
              </div>
            )}
          </label>
        );
      })}
    </div>
  );
};

const EquipmentBlock = ({ equipment, addEquipment, removeEquipment, patchEquipment }) => (
  <div className="space-y-2">
    {equipment.length === 0 && (
      <div className="bg-[#141414] border border-dashed border-[#3f3f3f] rounded-md p-4 text-center">
        <Wrench className="w-5 h-5 text-[#525252] mx-auto mb-1" />
        <p className="text-[#A3A3A3] text-xs">No equipment captured yet. Add the water heater, sump pump, softener, etc. you identified at this property.</p>
      </div>
    )}
    {equipment.map((eq, idx) => (
      <div key={idx} data-testid={`wiz-eq-${idx}`} className="bg-[#141414] border border-[#262626] rounded-md p-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider">Asset #{idx + 1}</p>
          <Button size="sm" variant="ghost" onClick={() => removeEquipment(idx)} className="text-red-400 hover:bg-red-900/20 h-7 w-7 p-0">
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Select value={eq.type} onValueChange={(v) => patchEquipment(idx, { type: v })}>
            <SelectTrigger data-testid={`wiz-eq-${idx}-type`} className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              {ASSET_TYPES.map((t) => <SelectItem key={t.key} value={t.key} className="text-white">{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid={`wiz-eq-${idx}-serial`} value={eq.serial} onChange={(e) => patchEquipment(idx, { serial: e.target.value })} placeholder="Serial # (primary ID)" className="bg-[#0A0A0A] border-[#262626] text-white" />
          <Input value={eq.brand} onChange={(e) => patchEquipment(idx, { brand: e.target.value })} placeholder="Manufacturer" className="bg-[#0A0A0A] border-[#262626] text-white" />
          <Input value={eq.model} onChange={(e) => patchEquipment(idx, { model: e.target.value })} placeholder="Model" className="bg-[#0A0A0A] border-[#262626] text-white" />
          <Input type="date" value={eq.install_date} onChange={(e) => patchEquipment(idx, { install_date: e.target.value })} placeholder="Install date" className="bg-[#0A0A0A] border-[#262626] text-white" />
          <Input type="date" value={eq.warranty_until} onChange={(e) => patchEquipment(idx, { warranty_until: e.target.value })} placeholder="Warranty until" className="bg-[#0A0A0A] border-[#262626] text-white" />
          <Select value={eq.condition || 'good'} onValueChange={(v) => patchEquipment(idx, { condition: v })}>
            <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              {['excellent', 'good', 'fair', 'poor', 'failing'].map((c) => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={eq.location} onChange={(e) => patchEquipment(idx, { location: e.target.value })} placeholder="Where (e.g. basement)" className="bg-[#0A0A0A] border-[#262626] text-white" />
        </div>
        <Textarea value={eq.notes} onChange={(e) => patchEquipment(idx, { notes: e.target.value })} placeholder="Notes" rows={2} className="bg-[#0A0A0A] border-[#262626] text-white" />
        <AssetPhotoCapture photos={eq.photo_urls || []} onChange={(next) => patchEquipment(idx, { photo_urls: next })} />
        {!eq.serial && (
          <p className="text-[10px] text-[#D4AF37]/80 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> No serial? We'll auto-generate a NexFlow Asset ID.
          </p>
        )}
      </div>
    ))}
    <Button data-testid="wiz-add-equipment" onClick={addEquipment} variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 w-full">
      <Plus className="w-4 h-4 mr-1" /> Add Equipment
    </Button>
  </div>
);

export default CompletionWizard;
